import React, { useEffect, useMemo, useRef, useState } from 'react';
import { directQuery, queryCountSessions } from '../utils/directFetch';
import { getCurrentUser as getStoredUser, shortId, isStoreOwnedBySuperAdmin, isSuperAdminId, COUNTERTOP_APP_CREATOR_LABEL } from '../utils/userDisplay';
import supabase from '../config/supabase';
import { getPoEmailStatusInfo, getPoStatusPill, isFailureStatus } from '../utils/poEmailStatus';
import { businessHoursElapsed } from '../services/orderingHubService';
import DashboardMessages from './DashboardMessages';
import PriceChangeTicker from './PriceChangeTicker';
import VendorContactModal from './ordering/VendorContactModal';
import EmailImportButton from './EmailImportButton';
import { getTemplateKind, isEopTemplate, getTemplateNextDate, daysUntilDate, localDateStr } from '../utils/prepCardHelpers';
import { getSetupCatalogReadiness } from '../utils/setupCatalogReadiness';
import { inventoryPeriodConfigToCycle } from './cycleHelpers.mjs';
import ParsWizardModal from './ParsWizardModal';
import { buildParNormalizationPatch, isMissingParNormalizationColumnError } from '../utils/countUnitModel';
import { startOfDay, getDaysAhead, buildUpcomingVendorEvents } from '../utils/vendorSchedules';

const COLORS = {
  primary: '#C8B5A0',
  primaryDark: '#9D7F66',
  secondary: '#E8DDD0',
  dark: '#5A4A42',
  medium: '#8B7B73',
  muted: '#A89890',
  light: '#F5E2D0',
  offWhite: '#FAF6F2',
  surface: '#FFFFFF',
  page: '#F4EFEB',
  red: '#D56B61',
  yellow: '#D49A45',
  green: '#7EA64E',
  blue: '#5F8EC4'
};

const SECTION_ACCENTS = {
  setup: {
    sectionBg: '#FFFBF2',
    sectionBorder: '#EDD596',
    stripe: '#C8880C',
    headingColor: '#7A5A0A',
    kickerColor: '#9A7218',
    badgeBg: '#FEF0C0',
    badgeColor: '#7A5A0A',
    progressBar: '#C8880C',
  },
  upcomingCounts: {
    sectionBg: '#F0F7F7',
    sectionBorder: '#96C8C8',
    stripe: '#1E8888',
    headingColor: '#146060',
    kickerColor: '#1A7070',
    badgeBg: '#B8E4E4',
    badgeColor: '#146060',
  },
  completedCounts: {
    sectionBg: '#F3F8F1',
    sectionBorder: '#A8D098',
    stripe: '#3E7A28',
    headingColor: '#2C601C',
    kickerColor: '#3A6E28',
    badgeBg: '#C8E4BC',
    badgeColor: '#2C601C',
  },
  vendorActions: {
    sectionBg: '#FEF4F1',
    sectionBorder: '#EAA890',
    stripe: '#B84828',
    headingColor: '#8C3018',
    kickerColor: '#A04028',
    badgeBg: '#F4C8B4',
    badgeColor: '#8C3018',
  },
  vendorUpcoming: {
    sectionBg: '#FDFAF0',
    sectionBorder: '#E0CC72',
    stripe: '#9C8008',
    headingColor: '#6C5A08',
    kickerColor: '#806A10',
    badgeBg: '#F0E488',
    badgeColor: '#6C5A08',
  },
  messages: {
    sectionBg: '#F5F6FA',
    sectionBorder: '#C0C8D8',
    stripe: '#58689A',
    headingColor: '#3C4C7C',
    kickerColor: '#4A5A8A',
    badgeBg: '#D4DAE8',
    badgeColor: '#3C4C7C',
  },
};
const PRICE_CHANGE_SOURCE_LABELS = {
  ai_price_import: 'AI Import',
  import_approval: 'Import',
  item_editor: 'Item Editor',
  web_count: 'Web Count',
  mobile_count: 'Mobile Count',
  mobile_count_close: 'Mobile Count',
};

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

async function loadDashboardInvoiceActivity(storeId) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) return [];
  const base = window.SUPABASE_URL || 'https://hpmbxwbuoamijdyhpele.supabase.co';
  const response = await fetch(`${base}/functions/v1/store-invoice-processor`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: window.SUPABASE_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action: 'get_inbox', storeId }),
  });
  if (!response.ok) throw new Error(`Invoice activity request failed (${response.status})`);
  const payload = await response.json();
  const cutoff = Date.now() - THIRTY_DAYS_MS;
  return (payload?.history || []).filter((row) => {
    const timestamp = new Date(row.received_at || row.processed_at || 0).getTime();
    return Number.isFinite(timestamp) && timestamp >= cutoff;
  });
}

const INVOICE_STATUS_META = {
  received: { label: 'Processing', color: '#6A5A8A', bg: '#F0ECF7' },
  processing: { label: 'Processing', color: '#6A5A8A', bg: '#F0ECF7' },
  needs_review: { label: 'Needs review', color: '#8A5B08', bg: '#FFF2CC' },
  processed: { label: 'Complete', color: '#2F6B32', bg: '#EAF5E8' },
  partial: { label: 'Complete', color: '#2F6B32', bg: '#EAF5E8' },
  duplicate: { label: 'Needs review', color: '#8A5B08', bg: '#FFF2CC' },
  failed: { label: 'Not accepted', color: '#9A3F36', bg: '#FBEAE7' },
  rejected: { label: 'Not accepted', color: '#9A3F36', bg: '#FBEAE7' },
};

function DashboardActivityPanel({ invoices, priceChanges }) {
  const lineClamp = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
  const invoiceRows = invoices || [];
  const changeRows = (priceChanges || []).filter((row) => row.change_type === 'updated');

  return (
    <section style={{
      background: '#FFFFFF',
      border: '1px solid #D8C9BC',
      borderLeft: '4px solid #8B6A54',
      borderRadius: '12px',
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '440px',
    }}>
      <div style={{ padding: '0.8rem 0.9rem 0.65rem', borderBottom: '1px solid #EEE4DC' }}>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, color: COLORS.dark, fontSize: '0.92rem', ...lineClamp }}>
          Price changes &amp; invoice activity
        </div>
        <div style={{ color: COLORS.medium, fontSize: '0.66rem', marginTop: '0.14rem' }}>
          30-day roll-in window · Today back 30 days
        </div>
      </div>

      <div style={{ overflowY: 'auto', minHeight: 0, padding: '0.7rem 0.8rem 0.85rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.45rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.72rem', color: COLORS.dark }}>Invoice activity</div>
          <a href="/invoice-inbox.html" style={{ fontSize: '0.64rem', color: '#76543F', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Open inbox →
          </a>
        </div>
        {invoiceRows.length === 0 ? (
          <div style={{ color: COLORS.muted, fontSize: '0.68rem', padding: '0.45rem 0' }}>No invoice activity in the last 30 days.</div>
        ) : invoiceRows.map((invoice) => {
          const status = INVOICE_STATUS_META[invoice.status] || INVOICE_STATUS_META.rejected;
          const title = invoice.vendor_name || invoice.subject || invoice.from_email || 'Invoice';
          return (
            <a key={invoice.id} href={`/invoice-inbox.html?invoice=${encodeURIComponent(invoice.id)}`} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.55rem',
              padding: '0.42rem 0.48rem', marginBottom: '0.28rem', border: '1px solid #EEE4DC',
              borderRadius: '8px', textDecoration: 'none', color: 'inherit', minWidth: 0,
            }}>
              <div style={{ flex: 1, ...lineClamp }}>
                <div style={{ fontWeight: 650, fontSize: '0.69rem', color: COLORS.dark, ...lineClamp }}>{title}</div>
                <div style={{ fontSize: '0.61rem', color: COLORS.medium, marginTop: '0.08rem', ...lineClamp }}>
                  {invoice.invoice_number ? `Invoice ${invoice.invoice_number} · ` : ''}{invoice.received_at ? formatRelTime(new Date(invoice.received_at)) : 'Recent'}
                </div>
              </div>
              <span style={{ flexShrink: 0, borderRadius: '999px', padding: '0.16rem 0.42rem', fontSize: '0.58rem', fontWeight: 700, color: status.color, background: status.bg }}>
                {status.label}
              </span>
            </a>
          );
        })}

        <div style={{ borderTop: '1px solid #E8DDD0', margin: '0.75rem 0 0.55rem', paddingTop: '0.65rem', fontWeight: 700, fontSize: '0.72rem', color: COLORS.dark }}>
          Item price changes
        </div>
        {changeRows.length === 0 ? (
          <div style={{ color: COLORS.muted, fontSize: '0.68rem', padding: '0.2rem 0' }}>No item price changes in the last 30 days.</div>
        ) : changeRows.map((row) => {
          const oldPrice = Number(row.old_case_price || 0);
          const newPrice = Number(row.new_case_price || 0);
          const name = row.new_name || row.old_name || 'Inventory item';
          return (
            <div key={row.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.55rem', padding: '0.38rem 0.18rem', borderBottom: '1px solid #F2EBE5', minWidth: 0 }}>
              <div style={{ flex: 1, ...lineClamp }}>
                <div style={{ fontWeight: 650, fontSize: '0.68rem', color: COLORS.dark, ...lineClamp }}>{name}</div>
                <div style={{ fontSize: '0.6rem', color: COLORS.medium, marginTop: '0.08rem', ...lineClamp }}>
                  {row.invoice_vendor || PRICE_CHANGE_SOURCE_LABELS[row.source] || 'Price update'} · {row.changed_at ? formatRelTime(new Date(row.changed_at)) : 'Recent'}
                </div>
              </div>
              <div style={{ flexShrink: 0, textAlign: 'right', fontSize: '0.64rem', fontWeight: 700, color: newPrice >= oldPrice ? '#9A4F32' : '#2F6B32' }}>
                ${oldPrice.toFixed(2)} → ${newPrice.toFixed(2)}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function formatRelTime(date) {
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

const PREVIEW_LIMIT = 4;
const URGENCY_THEME = {
  today: {
    cardBg: '#F9EEEC',
    border: '#E1C2BC',
    badgeBg: '#EED5D1',
    badgeColor: '#8A4B43'
  },
  tomorrow: {
    cardBg: '#FCF4EA',
    border: '#E8CEAF',
    badgeBg: '#F4E0C9',
    badgeColor: '#8B6135'
  }
};

const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const formatDate = (value) => {
  if (!value) return 'Not set';
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return 'Not set';
  return dt.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateTime = (value) => {
  if (!value) return 'Not set';
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return 'Not set';
  return dt.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const parseTimeTo24Hour = (value) => {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    return trimmed.length === 5 ? `${trimmed}:00` : trimmed;
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return '';
  let hour = parseInt(match[1], 10);
  const minute = match[2];
  const meridiem = match[3].toUpperCase();
  if (meridiem === 'PM' && hour < 12) hour += 12;
  if (meridiem === 'AM' && hour === 12) hour = 0;
  return `${String(hour).padStart(2, '0')}:${minute}:00`;
};

const combineDateAndTime = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return null;
  const parsedTime = parseTimeTo24Hour(timeValue);
  if (!parsedTime) return null;
  const dt = new Date(`${dateValue}T${parsedTime}`);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const toShortUserId = shortId;

const normalizeStatus = (status) => String(status || '').toLowerCase();

const getUrgencyFromDates = (dateValues) => {
  const today = startOfDay();
  const diffs = (dateValues || [])
    .map((value) => {
      if (!value) return null;
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      return getDaysAhead(date, today);
    })
    .filter((diff) => diff === 0 || diff === 1);

  if (diffs.includes(0)) return 'today';
  if (diffs.includes(1)) return 'tomorrow';
  return null;
};

const getCurrentUserInfo = getStoredUser;

/**
 * Derive a chip label from an email address local-part.
 * "jane.doe@…" → "Jane D."
 * "john_smith@…" → "John S."
 * "info@…" → "Info"
 */
const emailToChipLabel = (email) => {
  if (!email) return null;
  const local = (email.split('@')[0] || '').replace(/\+.*$/, '');
  const parts = local.split(/[._\-]/).filter(Boolean);
  if (parts.length >= 2) {
    const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
    const lastInitial = parts[1].charAt(0).toUpperCase();
    return `${first} ${lastInitial}.`;
  }
  const name = local.charAt(0).toUpperCase() + local.slice(1).toLowerCase();
  return name || email;
};

const getAccessedByLabel = (session, currentUser, storeOwnedBySuperAdmin = false) => {
  const rawId = session.last_accessed_by || session.created_by;
  if (!rawId) return 'Unknown';
  // The super admin acting in a store they don't own shows as "Countertop App"
  // rather than the admin's name (or a bare UUID).
  if (!storeOwnedBySuperAdmin && isSuperAdminId(rawId)) return COUNTERTOP_APP_CREATOR_LABEL;
  if (currentUser && rawId === currentUser.id) return currentUser.compactName || currentUser.displayName;
  return session.last_accessed_by_name || session.created_by_name || toShortUserId(rawId);
};

const deriveSessionTiming = (session) => {
  const firstAccess = session.first_accessed_at
    || session.started_at
    || combineDateAndTime(session.session_date, session.count_commence_time)
    || session.created_at
    || null;

  const lastAccess = session.last_accessed_at
    || session.updated_at
    || session.completed_at
    || session.created_at
    || null;

  return { firstAccess, lastAccess };
};

const deriveSessionState = (session) => {
  const status = normalizeStatus(session.status);
  const totalItems = Math.max(0, Number(session.total_items || 0));
  const countedItems = Math.max(0, Number(session.items_counted || 0));
  const boundedCounted = Math.min(totalItems || countedItems, countedItems);
  const percent = totalItems > 0
    ? Math.max(0, Math.min(100, Math.round((boundedCounted / totalItems) * 100)))
    : 0;
  const uncountedItems = Math.max(0, totalItems - boundedCounted);
  const hasPartialProgress = boundedCounted > 0 && (totalItems === 0 || boundedCounted < totalItems);
  const hasExplicitStart = Boolean(session.started_at && !session.completed_at);
  const isCompleted = status === 'completed' || Boolean(session.completed_at) || (totalItems > 0 && boundedCounted >= totalItems);
  const isInProgress = !isCompleted && (status === 'in_progress' || hasPartialProgress || hasExplicitStart);
  return {
    status,
    totalItems,
    countedItems: boundedCounted,
    percent,
    uncountedItems,
    isCompleted,
    isInProgress
  };
};

const TaskAlertDashboard = () => {
  const [countSessions, setCountSessions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [recentPOs, setRecentPOs] = useState([]);
  const [stalledPOs, setStalledPOs] = useState([]);
  const [failureReasons, setFailureReasons] = useState({});
  const [loading, setLoading] = useState(true);
  // True when the active store is owned by the super admin; gates whether the
  // admin's own name (vs the "Countertop App" label) shows in "accessed by".
  const [storeOwnedBySuperAdmin, setStoreOwnedBySuperAdmin] = useState(false);
  const [storeId, setStoreId] = useState(null);
  const [storeName, setStoreName] = useState(() => {
    try {
      const currentStore = localStorage.getItem('currentStore');
      if (currentStore) {
        const parsed = JSON.parse(currentStore);
        if (parsed?.name) return parsed.name;
      }
    } catch (_) {}
    return localStorage.getItem('selectedStoreName') || '';
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [setupData, setSetupData] = useState({
    itemsMissingPrice: 0,
    totalItems: 0,
    vendorsMissingEmail: [],
    lowParOrders: [],
    unmappedSubcategories: 0,
    // Template readiness checks (surfaced for users who bypassed auto-template setup)
    vendorsWithoutOrderTemplates: [],
    hasAnyOrderTemplate: false,
    hasEopTemplate: false,
    eopTemplateCount: 0,
    orderTemplateCount: 0,
  });
  const [vendorContactModal, setVendorContactModal] = useState(null);
  const [countTemplates, setCountTemplates] = useState([]);
  const [storePeriodConfig, setStorePeriodConfig] = useState(null);
  const [prepStatuses, setPrepStatuses] = useState({});
  const [prepCardBusy, setPrepCardBusy] = useState({});
  const [templateItemsCache, setTemplateItemsCache] = useState({});
  const [dashParsWizardModal, setDashParsWizardModal] = useState(null);
  const dashParsResolverRef = useRef(null);
  const [priceChangeRows, setPriceChangeRows] = useState([]);
  const [invoiceActivityRows, setInvoiceActivityRows] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [expandedSections, setExpandedSections] = useState({
    inProgress: false,
    upcoming: false,
    completed: false,
    orders: false,
    recentOrders: false,
  });
  const [collapsedSections, setCollapsedSections] = useState({
    inProgress: true,
    upcoming: true,
    completed: true,
    orders: true,
    recentOrders: false,
  });
  const currentUser = useMemo(() => getCurrentUserInfo(), []);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const storeIdFromStorage = localStorage.getItem('selectedStoreId') || localStorage.getItem('storeId');
    if (storeIdFromStorage) {
      const parsedStoreId = parseInt(storeIdFromStorage, 10);
      setStoreId(parsedStoreId);
      loadData(parsedStoreId);
    } else {
      setLoading(false);
    }

    const applyStoreFromLocalStorage = () => {
      try {
        const currentStore = localStorage.getItem('currentStore');
        if (currentStore) {
          const parsed = JSON.parse(currentStore);
          if (parsed?.name) {
            setStoreName(parsed.name);
            if (parsed.id) {
              setStoreId(parsed.id);
              loadData(parsed.id);
            }
            return;
          }
        }
      } catch (_) {}
      const fallbackName = localStorage.getItem('selectedStoreName') || '';
      setStoreName(fallbackName);
      const newStoreId = localStorage.getItem('selectedStoreId');
      if (newStoreId) {
        const parsedId = parseInt(newStoreId, 10);
        setStoreId(parsedId);
        loadData(parsedId);
      }
    };

    const handleStorageEvent = (e) => {
      if (e.key === 'currentStore' || e.key === 'selectedStoreId' || e.key === 'selectedStoreName') {
        applyStoreFromLocalStorage();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') applyStoreFromLocalStorage();
    };

    window.addEventListener('storage', handleStorageEvent);
    window.addEventListener('storeSelected', applyStoreFromLocalStorage);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('storage', handleStorageEvent);
      window.removeEventListener('storeSelected', applyStoreFromLocalStorage);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Keep team chips fresh: subscribe to INSERT/UPDATE/DELETE on store_users
  // and store_invites for the active store and re-run loadData when changes
  // arrive. Debounced so a burst of events only triggers one refresh.
  useEffect(() => {
    if (!storeId) return undefined;

    let debounceTimer = null;
    const scheduleRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        loadData(storeId);
      }, 400);
    };

    const filter = `store_id=eq.${storeId}`;
    const channel = supabase
      .channel(`team-chips-store-${storeId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'store_users', filter }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'store_invites', filter }, scheduleRefresh)
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [storeId]);

  const loadData = async (targetStoreId) => {
    try {
      const [sessionsRes, vendorRes, storeRes, storeUsersRes, itemsRes, mappingsRes] = await Promise.all([
        queryCountSessions(targetStoreId, { order: { column: 'session_date', ascending: false } }),
        directQuery('vendors', {
          eq: { store_id: targetStoreId, status: 'active' },
          order: { column: 'display_name', ascending: true }
        }),
        directQuery('stores', { eq: { id: targetStoreId }, select: 'id,name', limit: 1 }),
        supabase.from('store_users').select('user_id,joined_at')
          .eq('store_id', targetStoreId).eq('is_active', true)
          .then((r) => ({ data: r.data || [], error: r.error }))
          .catch(() => ({ data: [] })),
        directQuery('inventory_items', {
          eq: { store_id: targetStoreId },
          select: 'id,par_level,unit_price,case_price,category,is_sample_data,vendor'
        }),
        directQuery('pnl_category_mappings', {
          eq: { store_id: targetStoreId },
          select: 'inventory_category'
        }).catch(() => ({ data: [] })),
      ]);

      // Two-step team load: store_users.user_id has FK to auth.users, not user_profiles.
      // Use supabase client (not directQuery) so the user's JWT is sent and RLS allows
      // seeing all store members, not just the viewer's own row.
      let profilesRes = { data: [] };
      let invitesRes = { data: [] };
      let memberAuthEmails = [];
      const userIds = (storeUsersRes.data || []).map((r) => r.user_id).filter(Boolean);
      const teamFetches = [];
      if (userIds.length > 0) {
        teamFetches.push(
          supabase.from('user_profiles').select('id,display_name,first_name,last_name,email')
            .in('id', userIds)
            .then((r) => { profilesRes = { data: r.data || [], error: r.error }; })
            .catch((err) => {
              console.warn('⚠️ [TaskAlertDashboard] user_profiles lookup failed:', err);
            })
        );
        // Fetch auth emails for all active members via SECURITY DEFINER RPC
        // (migration 20260503100000). This is the authoritative email source
        // for members with blank user_profiles.email.
        teamFetches.push(
          supabase.rpc('get_store_member_emails', { p_store_id: targetStoreId })
            .then((r) => { memberAuthEmails = r.data || []; })
            .catch(() => { /* non-critical */ })
        );
      }
      teamFetches.push(
        // Fetch all invites (all statuses) so accepted invites can be used as
        // a name/email fallback for active members who have no user_profiles row.
        supabase.from('store_invites').select('email,status,first_name,last_name,user_id')
          .eq('store_id', targetStoreId)
          .then((r) => { invitesRes = { data: r.data || [], error: r.error }; })
          .catch((err) => {
            console.warn('⚠️ [TaskAlertDashboard] store_invites lookup failed:', err);
          })
      );
      await Promise.all(teamFetches);

      const sessions = sessionsRes.data || [];
      const vendorData = vendorRes.data || [];
      const items = itemsRes.data || [];
      const mappedCategories = new Set((mappingsRes.data || []).map((m) => (m.inventory_category || '').trim().toLowerCase()));
      const allCategories = [...new Set(items.map((i) => i.category).filter(Boolean))];
      const unmappedSubcategories = allCategories.filter(
        (cat) => !mappedCategories.has(cat.trim().toLowerCase())
      ).length;
      setCountSessions(sessions);
      isStoreOwnedBySuperAdmin(supabase, targetStoreId)
        .then(setStoreOwnedBySuperAdmin)
        .catch(() => setStoreOwnedBySuperAdmin(false));
      setVendors(vendorData);
      const fetchedName = storeRes.data && storeRes.data[0] && storeRes.data[0].name;
      if (fetchedName) setStoreName(fetchedName);

      // Build profile lookup map
      const profileMap = {};
      (profilesRes.data || []).forEach((p) => { profileMap[p.id] = p; });

      // Build auth-email lookup: user_id → auth email from RPC.
      // This is the authoritative email for members whose user_profiles.email is blank.
      const authEmailByUserId = {};
      (memberAuthEmails || []).forEach((row) => {
        if (row.user_id && row.email) authEmailByUserId[row.user_id] = row.email.toLowerCase();
      });

      // Augment profileMap with auth emails so the invite lookup can use them.
      Object.entries(authEmailByUserId).forEach(([uid, authEmail]) => {
        if (profileMap[uid]) {
          profileMap[uid] = { ...profileMap[uid], email: profileMap[uid].email || authEmail };
        } else {
          profileMap[uid] = { id: uid, email: authEmail, first_name: '', last_name: '' };
        }
      });

      // Build invite lookup maps from ALL invite rows (all statuses).
      // Primary key: invite.user_id (for accepted invites with user_id set).
      // Secondary key: invite.email, which also enables reverse-matching for
      // historical invites where user_id is null but auth email is now known.
      const inviteByMemberUserId = {};
      const inviteByMemberEmail = {};
      (invitesRes.data || []).forEach((inv) => {
        if (inv.user_id) inviteByMemberUserId[inv.user_id] = inv;
        if (inv.email) inviteByMemberEmail[(inv.email || '').toLowerCase()] = inv;
      });
      // For invites with null user_id, bind via auth email (reverse lookup).
      const userIdByKnownEmail = {};
      Object.entries(authEmailByUserId).forEach(([uid, authEmail]) => {
        if (authEmail) userIdByKnownEmail[authEmail.toLowerCase()] = uid;
      });
      Object.entries(profileMap).forEach(([uid, prof]) => {
        if (prof.email) userIdByKnownEmail[(prof.email || '').toLowerCase()] = uid;
      });
      (invitesRes.data || []).forEach((inv) => {
        if (!inv.user_id && inv.email) {
          const matchedUid = userIdByKnownEmail[(inv.email || '').toLowerCase()];
          if (matchedUid && !inviteByMemberUserId[matchedUid]) {
            inviteByMemberUserId[matchedUid] = inv;
          }
        }
      });

      // Track active member emails to de-dup against pending invites
      const activeMemberEmails = new Set();

      // Build active member chips — never drop a member for missing name data
      const activeTeam = (storeUsersRes.data || []).map((su) => {
        const userId = su.user_id;
        let profile = profileMap[userId];

        // Current viewer fallback: if profile is missing/empty, use localStorage metadata
        if (userId === currentUser?.id) {
          const meta = currentUser?.user_metadata || {};
          const localDisplay = (currentUser?.display_name || meta.display_name || '').trim();
          const localFirst = (currentUser?.first_name || meta.first_name || '').trim();
          const localLast = (currentUser?.last_name || meta.last_name || '').trim();
          const localEmail = (currentUser?.email || '').trim();
          if (!profile || (!profile.display_name && !profile.first_name && !profile.email)) {
            profile = { display_name: localDisplay, first_name: localFirst, last_name: localLast, email: localEmail };
          } else {
            profile = {
              ...profile,
              display_name: profile.display_name || localDisplay,
              first_name: profile.first_name || localFirst,
              last_name: profile.last_name || localLast,
              email: profile.email || localEmail
            };
          }
        }

        // Invite-based fallback: look up the invite that added this member, first by
        // user_id, then by their known email (profile email or auth email from RPC).
        const profileEmail = ((profile && profile.email) || authEmailByUserId[userId] || '').trim();
        const invMatch = inviteByMemberUserId[userId]
          || (profileEmail ? inviteByMemberEmail[profileEmail.toLowerCase()] : null);

        const displayName = ((profile && profile.display_name) || '').trim();
        const first = ((profile && profile.first_name) || (invMatch && invMatch.first_name) || '').trim();
        const last = ((profile && profile.last_name) || (invMatch && invMatch.last_name) || '').trim();
        const email = (profileEmail || (invMatch && invMatch.email) || (userId === currentUser?.id ? currentUser?.email : '') || '').trim();

        if (email) activeMemberEmails.add(email.toLowerCase());

        let label;
        if (displayName) {
          const parts = displayName.split(/\s+/).filter(Boolean);
          label = parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
        }
        else if (first && last) label = `${first} ${last[0]}.`;
        else if (first) label = first;
        else if (email) label = emailToChipLabel(email) || email;
        else label = 'Teammate';

        const fifteenDaysAgo = new Date(Date.now() - 15 * 24 * 60 * 60 * 1000);
        const welcome = su.joined_at ? new Date(su.joined_at) >= fifteenDaysAgo : false;

        return { label, pending: false, welcome };
      });

      // Pending invite chips — only sent/pending status, de-duped against active members
      const PENDING_STATUSES = new Set(['sent', 'pending']);
      const pendingTeam = (invitesRes.data || [])
        .filter((inv) => PENDING_STATUSES.has((inv.status || '').toLowerCase()) && !activeMemberEmails.has((inv.email || '').toLowerCase()))
        .map((inv) => {
          const email = (inv.email || '').trim();
          if (!email) return null;
          const invFirst = (inv.first_name || '').trim();
          const invLast = (inv.last_name || '').trim();
          let label;
          if (invFirst && invLast) label = `${invFirst} ${invLast[0]}.`;
          else if (invFirst) label = invFirst;
          else label = emailToChipLabel(email) || email;
          return { label, pending: true };
        }).filter(Boolean);

      // Guarantee the viewer always appears — prepend if not already in the active list
      const viewerInActive =
        (storeUsersRes.data || []).some((su) => su.user_id === currentUser?.id) ||
        !!(currentUser?.email && activeMemberEmails.has((currentUser.email || '').toLowerCase()));
      if (!viewerInActive && currentUser) {
        const meta = currentUser.user_metadata || {};
        const localDisplay = (currentUser.display_name || meta.display_name || '').trim();
        const localFirst = (currentUser.first_name || meta.first_name || '').trim();
        const localLast = (currentUser.last_name || meta.last_name || '').trim();
        const localEmail = (currentUser.email || '').trim();
        let viewerLabel;
        if (localDisplay) {
          const parts = localDisplay.split(/\s+/).filter(Boolean);
          viewerLabel = parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
        }
        else if (localFirst && localLast) viewerLabel = `${localFirst} ${localLast[0]}.`;
        else if (localFirst) viewerLabel = localFirst;
        else if (localEmail) viewerLabel = emailToChipLabel(localEmail) || localEmail;
        else viewerLabel = 'Teammate';
        activeTeam.unshift({ label: viewerLabel, pending: false, welcome: false });
      }

      setTeamMembers([...activeTeam, ...pendingTeam]);

      // Setup checks
      const itemsMissingPrice = items.filter((i) => {
        const u = parseFloat(i.unit_price);
        const c = parseFloat(i.case_price);
        return (!u || u <= 0) && (!c || c <= 0);
      }).length;

      const vendorsMissingEmail = vendorData
        .filter((v) => {
          const hasEmail = Boolean(v.contact_email && String(v.contact_email).trim());
          const hasPhone = Boolean(v.contact_phone && String(v.contact_phone).trim());
          const hasName = Boolean(v.contact_name && String(v.contact_name).trim());
          return !hasEmail && !hasPhone && !hasName;
        })
        .map((v) => ({ id: v.id, name: v.display_name || v.name || 'Unnamed vendor' }));

      // Low-PAR ordering sessions: only sessions whose count_purpose === 'ordering'
      const orderingSessions = sessions.filter((s) => s.count_purpose === 'ordering');
      let lowParOrders = [];
      if (orderingSessions.length > 0) {
        const orderIds = orderingSessions.slice(0, 100).map((s) => s.id);
        let ciData = [];
        try {
          const ciRes = await directQuery('count_items', {
            in: { count_session_id: orderIds },
            select: 'count_session_id,inventory_item_id'
          });
          ciData = ciRes.data || [];
        } catch (err) {
          console.warn('⚠️ [TaskAlertDashboard] count_items lookup failed:', err);
        }
        const parSet = new Set(items.filter((i) => Number(i.par_level) > 0).map((i) => i.id));
        // Track distinct inventory_item_id per session so duplicate count_items
        // rows (multiple locations / recounts) don't skew the PAR-coverage ratio.
        const perSession = new Map();
        ciData.forEach((ci) => {
          let e = perSession.get(ci.count_session_id);
          if (!e) {
            e = { itemIds: new Set() };
            perSession.set(ci.count_session_id, e);
          }
          if (ci.inventory_item_id != null) e.itemIds.add(ci.inventory_item_id);
        });
        lowParOrders = orderingSessions
          .map((s) => {
            const e = perSession.get(s.id);
            const itemIds = e ? Array.from(e.itemIds) : [];
            const total = itemIds.length;
            const withPar = itemIds.filter((id) => parSet.has(id)).length;
            const ratio = total > 0 ? withPar / total : 0;
            return {
              id: s.id,
              name: s.session_name || s.name || 'Untitled order',
              created_at: s.created_at,
              withPar,
              total,
              ratio,
            };
          })
          .filter((x) => x.total > 0 && x.ratio < 0.5)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      }

      setSetupData((prev) => ({
        ...prev,
        itemsMissingPrice,
        totalItems: items.filter((i) => !i.is_sample_data).length,
        vendorsMissingEmail,
        lowParOrders,
        unmappedSubcategories,
        // Template readiness fields are filled in by the template-fetch block below;
        // keep prior values here so they survive this state update.
      }));

      try {
        const poFields = `id, vendor_name_snapshot, vendor_contact_snapshot, ordered_by_name, total, sent_at, delivered_at, opened_at, clicked_at, bounced_at, complained_at, delayed_at, delivery_status, requested_delivery_date, created_at, line_count, po_number, is_latest_revision, archived_at`;
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        // Rough cutoff: 24 real hours ago (business-hours filtering applied client-side)
        const stalledCutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

        // Actionable POs: drafts, blocked (no vendor email), failed/bounced/complained/delayed.
        // is_latest_revision=true excludes superseded revisions; archived_at IS NULL excludes
        // archived POs — aligns with the orderingHubService query guards.
        const { data: actionableRows, error: actionErr } = await supabase
          .from('purchase_orders')
          .select(poFields)
          .eq('store_id', targetStoreId)
          .in('delivery_status', ['draft', 'blocked_no_email', 'bounced', 'complained', 'error', 'failed', 'blocked', 'delivery_delayed'])
          .eq('is_latest_revision', true)
          .is('archived_at', null)
          .order('created_at', { ascending: false })
          .limit(15);
        if (actionErr) console.warn('⚠️ [TaskAlertDashboard] actionable PO lookup failed:', actionErr);

        // Sent today POs
        const { data: sentTodayRows, error: sentErr } = await supabase
          .from('purchase_orders')
          .select(poFields)
          .eq('store_id', targetStoreId)
          .gte('sent_at', todayStart.toISOString())
          .not('sent_at', 'is', null)
          .order('sent_at', { ascending: false })
          .limit(10);
        if (sentErr) console.warn('⚠️ [TaskAlertDashboard] sent-today PO lookup failed:', sentErr);

        // Potentially stalled POs: sent 24+ real hours ago, not opened, latest revision only
        const { data: stalledRows, error: stalledErr } = await supabase
          .from('purchase_orders')
          .select(poFields)
          .eq('store_id', targetStoreId)
          // delivery_delayed is excluded: those POs already appear in the Failed/Bounced bucket.
        .in('delivery_status', ['sent', 'delivered'])
          .eq('is_latest_revision', true)
          .is('archived_at', null)
          .is('opened_at', null)
          .not('sent_at', 'is', null)
          .lt('sent_at', stalledCutoff)
          .order('sent_at', { ascending: false })
          .limit(15);
        if (stalledErr) console.warn('⚠️ [TaskAlertDashboard] stalled PO lookup failed:', stalledErr);
        // Apply business-hours filter: only flag if 24+ Mon–Fri hours have passed
        const confirmedStalled = (stalledRows || []).filter((po) => businessHoursElapsed(po.sent_at) >= 24);
        setStalledPOs(confirmedStalled);

        // Batch-fetch the most recent failure event for each failed/bounced/delayed PO
        // so the dashboard can show a human-readable reason on the alert card.
        const failedRows = (actionableRows || []).filter((r) =>
          ['bounced', 'complained', 'error', 'failed', 'delivery_delayed'].includes(r.delivery_status),
        );
        if (failedRows.length === 0) {
          setFailureReasons({});
        } else {
          const { data: evRows } = await supabase
            .from('purchase_order_delivery_events')
            .select('purchase_order_id, event_type, payload, occurred_at')
            .in('purchase_order_id', failedRows.map((r) => r.id))
            .in('event_type', ['email.bounced', 'email.complained', 'email.delivery_delayed', 'email.error'])
            .order('occurred_at', { ascending: false });
          const reasonMap = {};
          (evRows || []).forEach((ev) => {
            if (reasonMap[ev.purchase_order_id]) return;
            const d = (ev.payload?.data) || ev.payload || {};
            const reason =
              d.reason ||
              d.error?.message ||
              d.error ||
              ev.payload?.reason ||
              ev.event_type?.replace('email.', '') ||
              null;
            if (reason) reasonMap[ev.purchase_order_id] = String(reason);
          });
          setFailureReasons(reasonMap);
        }

        // Merge actionable + sentToday, deduplicate by id
        const seen = new Set();
        const merged = [];
        for (const row of [...(actionableRows || []), ...(sentTodayRows || [])]) {
          if (!seen.has(row.id)) { seen.add(row.id); merged.push(row); }
        }
        setRecentPOs(merged);
      } catch (poError) {
        console.warn('⚠️ [TaskAlertDashboard] purchase_orders lookup threw:', poError);
        setRecentPOs([]);
        setStalledPOs([]);
      }

      try {
        const sinceIso = new Date(Date.now() - THIRTY_DAYS_MS).toISOString();
        const [{ data: phRows }, { data: phItems }] = await Promise.all([
          supabase
            .from('price_history')
            .select('id,changed_at,old_case_price,new_case_price,price_delta_pct,source,invoice_vendor,inventory_item_id,change_type,new_name,old_name')
            .eq('store_id', targetStoreId)
            .in('change_type', ['updated', 'setup'])
            .gte('changed_at', sinceIso)
            .order('changed_at', { ascending: false })
            .limit(2000),
          supabase
            .from('inventory_items')
            .select('id,description,vendor,unit')
            .eq('store_id', targetStoreId),
        ]);
        const invMap = new Map((phItems || []).map((i) => [i.id, i]));
        const enriched = (phRows || []).map((row) => {
          const inv = row.inventory_item_id ? invMap.get(row.inventory_item_id) : null;
          return {
            ...row,
            new_name: inv?.description || row.new_name || row.old_name,
            invoice_vendor: inv?.vendor || row.invoice_vendor,
            unit: inv?.unit || null,
          };
        });
        setPriceChangeRows(enriched);
      } catch (phErr) {
        console.warn('⚠️ [TaskAlertDashboard] price history feed failed:', phErr);
      }

      try {
        setInvoiceActivityRows(await loadDashboardInvoiceActivity(targetStoreId));
      } catch (invoiceActivityError) {
        console.warn('⚠️ [TaskAlertDashboard] invoice activity feed failed:', invoiceActivityError);
        setInvoiceActivityRows([]);
      }

      // Load count templates + store period config for pre-count prep cards
      try {
        const todayForPrep = new Date(); todayForPrep.setHours(0, 0, 0, 0);
        const in7Days = new Date(todayForPrep); in7Days.setDate(todayForPrep.getDate() + 7);
        const todayPrepStr = localDateStr(todayForPrep);
        const in7DaysStr = localDateStr(in7Days);
        const in3DaysStr = localDateStr(new Date(todayForPrep.getTime() + 3 * 86400000));
        const [tmplRes, periodRes, prepRes] = await Promise.all([
          // Templates live in count_sessions with is_template=true — that's where
          // ordering_cycle_config (the schedule) is persisted (see migration
          // 20260421120000 and persistScheduleToTemplate in CountInterface.jsx).
          // The legacy count_templates table does not hold the schedule.
          supabase.from('count_sessions')
            .select('id,name,order_calculations_enabled,ordering_cycle_config,count_purpose,inventory_mode,updated_at')
            .eq('store_id', targetStoreId)
            .eq('is_template', true)
            .not('status', 'in', '(cancelled,deleted)'),
          supabase.from('stores').select('inventory_period_config').eq('id', targetStoreId).single(),
          supabase.from('count_prep_status')
            .select('*')
            .eq('store_id', targetStoreId)
            .gte('scheduled_date', todayPrepStr)
            .lte('scheduled_date', in7DaysStr)
        ]);
        const allTemplates = tmplRes.data || [];
        const periodCfg = periodRes.data?.inventory_period_config ?? null;
        setCountTemplates(allTemplates);
        setStorePeriodConfig(periodCfg);

        // Template setup checks are shown for users who bypassed onboarding/auto-template
        // creation. This is dashboard-only for now; AI setup review will later consume the
        // same readiness data.
        //
        // Detection rule (same conventions the templates page uses to group cards):
        //   • order template → order_calculations_enabled is true
        //   • EOP template   → isEopTemplate(t): count_purpose period_end/full_inventory,
        //     inventory_mode Financial/Both, or a legacy eop_454/eop_544 preset.
        // EOP is detected by how the template is USED, not by its schedule preset —
        // otherwise an EOP template on a normal weekly schedule (every_friday) would
        // be missed and the card would wrongly say "End of Period is not set up".
        const orderTemplateCount = allTemplates.filter(
          (t) => getTemplateKind(t) === 'order' || t.order_calculations_enabled === true
        ).length;
        const eopTemplateCount = allTemplates.filter(isEopTemplate).length;

        // Real vendor names that appear on active (non-sample) inventory items. Blank and
        // placeholder vendor labels are ignored so they don't trigger a false warning.
        const PLACEHOLDER_VENDOR_NAMES = new Set([
          'n/a', 'na', 'none', 'no vendor', 'unassigned', 'unknown', 'default', 'misc', 'tbd', 'test',
        ]);
        const vendorItemCounts = new Map();
        items
          .filter((i) => !i.is_sample_data)
          .forEach((i) => {
            const name = (i.vendor == null ? '' : String(i.vendor)).trim();
            if (!name) return;
            if (PLACEHOLDER_VENDOR_NAMES.has(name.toLowerCase())) return;
            vendorItemCounts.set(name, (vendorItemCounts.get(name) || 0) + 1);
          });

        // First-pass logic: if there are real vendor names but zero order templates, flag every
        // such vendor. (Partial per-vendor template coverage is intentionally out of scope here.)
        const vendorsWithoutOrderTemplates = (vendorItemCounts.size > 0 && orderTemplateCount === 0)
          ? Array.from(vendorItemCounts.entries())
              .map(([vendorName, itemCount]) => ({ vendorName, itemCount }))
              .sort((a, b) => b.itemCount - a.itemCount)
          : [];

        setSetupData((prev) => ({
          ...prev,
          vendorsWithoutOrderTemplates,
          hasAnyOrderTemplate: orderTemplateCount > 0,
          hasEopTemplate: eopTemplateCount > 0,
          eopTemplateCount,
          orderTemplateCount,
        }));

        const statusMap = {};
        (prepRes.data || []).forEach((row) => {
          statusMap[`${row.template_id}:${row.scheduled_date}`] = row;
        });

        // Determine which templates qualify for a prep card (next date ≤ 3 days).
        // When a session already exists for a template+date, auto-complete the create_count
        // step so the card flips to "Count created → Open count" instead of disappearing.
        const qualifyingCards = [];
        for (const t of allTemplates) {
          const nextDate = getTemplateNextDate(t, periodCfg);
          if (!nextDate || nextDate < todayPrepStr || nextDate > in3DaysStr) continue;
          const matchingSession = sessions.find(
            (s) => String(s.template_id) === String(t.id) &&
              s.session_date === nextDate &&
              !['cancelled', 'deleted'].includes((s.status || '').toLowerCase())
          );
          const statusKey = `${t.id}:${nextDate}`;
          if (matchingSession) {
            // Session exists — auto-complete create_count if there's a prep status row
            const sr = statusMap[statusKey];
            if (sr && !sr.create_count_completed_at) {
              const now = new Date().toISOString();
              statusMap[statusKey] = { ...sr, create_count_completed_at: now, created_session_id: matchingSession.id };
              supabase.from('count_prep_status')
                .update({ create_count_completed_at: now, created_session_id: matchingSession.id })
                .eq('id', sr.id).catch(() => {});
            }
            // Only include the card if create_count is now marked done (so it shows "Count created → Open count")
            if (statusMap[statusKey]?.create_count_completed_at) {
              qualifyingCards.push({ template: t, scheduledDate: nextDate });
            }
          } else {
            qualifyingCards.push({ template: t, scheduledDate: nextDate });
          }
        }

        // Lazy-create stub prep rows for qualifying templates that have no row yet
        const stubInserts = qualifyingCards
          .filter(({ template, scheduledDate }) => !statusMap[`${template.id}:${scheduledDate}`])
          .map(({ template, scheduledDate }) => ({
            store_id: targetStoreId,
            template_id: template.id,
            scheduled_date: scheduledDate,
            template_kind: getTemplateKind(template),
          }));
        if (stubInserts.length > 0) {
          const { data: newRows } = await supabase
            .from('count_prep_status')
            .upsert(stubInserts, { onConflict: 'store_id,template_id,scheduled_date', ignoreDuplicates: true })
            .select('*');
          (newRows || []).forEach((row) => {
            statusMap[`${row.template_id}:${row.scheduled_date}`] = row;
          });
        }

        // Auto-complete review_items when template.updated_at is newer than prep row creation —
        // this means the user saved a template change after the prep row was first created.
        for (const [key, row] of Object.entries(statusMap)) {
          if (!row.review_items_completed_at && row.created_at) {
            const tid = key.split(':')[0];
            const tmpl = allTemplates.find((t) => String(t.id) === tid);
            if (tmpl?.updated_at && new Date(tmpl.updated_at) > new Date(row.created_at)) {
              const now = new Date().toISOString();
              statusMap[key] = { ...row, review_items_completed_at: now };
              // TODO(notifications): fire `prep_step_completed:review_items` event
              supabase.from('count_prep_status')
                .update({ review_items_completed_at: now })
                .eq('id', row.id)
                .catch(() => {});
            }
          }
        }

        // Auto-complete update_prices per-template.
        // Primary signal: localStorage key written by UpdatePrices after a successful apply
        // when the user navigated there via ?template=<id> from a prep card.
        // Fallback signal: price_history entries for that template's items after opened_at.
        const rowsNeedingPriceCheck = Object.values(statusMap)
          .filter((r) => r.update_prices_opened_at && !r.update_prices_completed_at && r.id);
        if (rowsNeedingPriceCheck.length > 0) {
          const now = new Date().toISOString();

          // ── Primary: consume the direct localStorage completion signal ──
          let localSignal = null;
          try {
            const raw = localStorage.getItem('prep_price_update_signal');
            if (raw) localSignal = JSON.parse(raw);
          } catch (_) {}
          if (localSignal?.templateId && localSignal?.storeId === String(targetStoreId)) {
            const signalRow = rowsNeedingPriceCheck.find(
              (r) => String(r.template_id) === String(localSignal.templateId) &&
                r.update_prices_opened_at &&
                localSignal.completedAt > r.update_prices_opened_at
            );
            if (signalRow) {
              const key = `${signalRow.template_id}:${signalRow.scheduled_date}`;
              statusMap[key] = { ...signalRow, update_prices_completed_at: localSignal.completedAt };
              // TODO(notifications): fire `prep_step_completed:update_prices` event
              supabase.from('count_prep_status')
                .update({ update_prices_completed_at: localSignal.completedAt })
                .eq('id', signalRow.id)
                .catch(() => {});
              // Consume the signal so it doesn't re-fire on subsequent loads
              try { localStorage.removeItem('prep_price_update_signal'); } catch (_) {}
            }
          }

          // ── Fallback: per-template price_history check for any remaining rows ──
          const stillNeedingCheck = Object.values(statusMap)
            .filter((r) => r.update_prices_opened_at && !r.update_prices_completed_at && r.id);
          if (stillNeedingCheck.length > 0) {
            const templateIdsForPriceCheck = [...new Set(stillNeedingCheck.map((r) => r.template_id))];
            const { data: templateItemRows } = await supabase
              .from('count_template_items')
              .select('template_id,inventory_item_id')
              .in('template_id', templateIdsForPriceCheck);
            const itemsByTemplate = {};
            (templateItemRows || []).forEach(({ template_id, inventory_item_id }) => {
              if (!itemsByTemplate[template_id]) itemsByTemplate[template_id] = [];
              itemsByTemplate[template_id].push(inventory_item_id);
            });
            for (const row of stillNeedingCheck) {
              const itemIds = itemsByTemplate[row.template_id] || [];
              if (itemIds.length === 0) continue;
              const { data: priceHits } = await supabase
                .from('price_history')
                .select('id')
                .eq('store_id', targetStoreId)
                .in('inventory_item_id', itemIds)
                .gte('changed_at', row.update_prices_opened_at)
                .limit(1);
              if (priceHits?.length > 0) {
                const key = `${row.template_id}:${row.scheduled_date}`;
                statusMap[key] = { ...row, update_prices_completed_at: now };
                // TODO(notifications): fire `prep_step_completed:update_prices` event
                supabase.from('count_prep_status')
                  .update({ update_prices_completed_at: now })
                  .eq('id', row.id)
                  .catch(() => {});
              }
            }
          }
        }

        setPrepStatuses(statusMap);
      } catch (prepErr) {
        console.warn('⚠️ [TaskAlertDashboard] prep card data failed:', prepErr);
      }
    } catch (error) {
      console.error('❌ [TaskAlertDashboard] Failed loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sessionBuckets = useMemo(() => {
    const today = startOfDay();
    const upcomingCutoff = new Date(today);
    upcomingCutoff.setDate(today.getDate() + 14);

    const inProgress = [];
    const upcoming = [];
    const completed = [];

    countSessions.forEach((session) => {
      const state = deriveSessionState(session);
      const timing = deriveSessionTiming(session);
      const sessionDate = startOfDay(session.session_date || session.created_at || today);
      const enriched = {
        ...session,
        ...state,
        ...timing,
        sessionDate,
        accessedByLabel: getAccessedByLabel(session, currentUser, storeOwnedBySuperAdmin)
      };

      if (state.isInProgress) {
        inProgress.push(enriched);
      } else if (state.isCompleted) {
        completed.push(enriched);
      } else if (sessionDate >= today && sessionDate <= upcomingCutoff) {
        upcoming.push(enriched);
      }
    });

    inProgress.sort((a, b) => {
      const aTime = new Date(a.lastAccess || a.created_at).getTime();
      const bTime = new Date(b.lastAccess || b.created_at).getTime();
      return bTime - aTime;
    });

    upcoming.sort((a, b) => a.sessionDate - b.sessionDate);
    completed.sort((a, b) => {
      const aTime = new Date(a.completed_at || a.lastAccess || a.updated_at).getTime();
      const bTime = new Date(b.completed_at || b.lastAccess || b.updated_at).getTime();
      return bTime - aTime;
    });

    return {
      inProgress,
      upcoming,
      completed: completed.slice(0, 10)
    };
  }, [countSessions, currentUser, storeOwnedBySuperAdmin]);

  const tickerEntries = useMemo(() => {
    const updatedRows = priceChangeRows.filter((r) => r.change_type === 'updated');
    const latestByItem = new Map();
    // priceChangeRows is already ordered by changed_at DESC, so first hit is most recent
    for (const row of updatedRows) {
      const key = row.inventory_item_id || row.id;
      if (!latestByItem.has(key)) latestByItem.set(key, row);
      if (latestByItem.size >= 20) break; // cap early to avoid 2000-item map -> huge ticker width
    }
    return Array.from(latestByItem.values()).map((row) => ({
      name: row.new_name || row.old_name || '(unnamed)',
      vendor: row.invoice_vendor || '',
      unit: row.unit || '',
      oldPrice: parseFloat(row.old_case_price) || 0,
      newPrice: parseFloat(row.new_case_price) || 0,
      date: row.changed_at || null,
    }));
  }, [priceChangeRows]);

  const upcomingOrders = useMemo(() => buildUpcomingVendorEvents(vendors, 'order', 7), [vendors]);
  const sectionUrgency = useMemo(() => ({
    inProgress: getUrgencyFromDates(sessionBuckets.inProgress.map((session) => session.sessionDate)),
    upcoming: getUrgencyFromDates(sessionBuckets.upcoming.map((session) => session.sessionDate)),
    completed: getUrgencyFromDates(sessionBuckets.completed.map((session) => session.completed_at || session.sessionDate)),
    orders: getUrgencyFromDates(upcomingOrders.map((event) => event.date)),
  }), [sessionBuckets, upcomingOrders]);

  const prepCards = useMemo(() => {
    if (!countTemplates.length) return [];
    const todayD = new Date(); todayD.setHours(0, 0, 0, 0);
    const in3D = new Date(todayD); in3D.setDate(todayD.getDate() + 3);
    const todayStr = localDateStr(todayD);
    const in3DStr = localDateStr(in3D);
    const results = [];
    for (const template of countTemplates) {
      const nextDate = getTemplateNextDate(template, storePeriodConfig);
      if (!nextDate || nextDate < todayStr || nextDate > in3DStr) continue;
      const hasSession = countSessions.some(
        (s) =>
          String(s.template_id) === String(template.id) &&
          s.session_date === nextDate &&
          !['cancelled', 'deleted'].includes((s.status || '').toLowerCase())
      );
      if (hasSession) {
        // When a session exists, only keep the card if create_count is already
        // marked done in prepStatuses (so "Count created → Open count" shows).
        const statusKey = `${template.id}:${nextDate}`;
        if (!prepStatuses[statusKey]?.create_count_completed_at) continue;
      }
      results.push({ template, scheduledDate: nextDate });
    }
    results.sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
    return results;
  }, [countTemplates, storePeriodConfig, countSessions, prepStatuses]);

  // Publish the prep-active state to localStorage so the sidebar/mobile-nav
  // attention dot can read the dashboard's own template-based computation
  // rather than re-deriving it independently (which can't replicate the full
  // cycle/schedule logic in vanilla JS).
  // Key is store-scoped ('_ct_dash_prep_active:{storeId}') so switching stores
  // never shows stale data from the previous store. Sidebar/mobile read the
  // same key and fall back to a count_sessions query if the key is absent.
  useEffect(() => {
    if (!storeId) return;
    try {
      const key = '_ct_dash_prep_active:' + storeId;
      localStorage.setItem(key, prepCards.length > 0 ? '1' : '0');
      // Dispatch event so sidebar / mobile-nav event listeners re-render the dot.
      window.dispatchEvent(new CustomEvent('dashboard-prep-changed'));
      if (typeof window.__refreshDashboardPrepDot === 'function') {
        window.__refreshDashboardPrepDot();
      }
    } catch (_) {}
  }, [prepCards, storeId]);

  // Prefetch template items for order templates so parsMissing can be evaluated.
  // Runs whenever prepCards changes. Results are cached in templateItemsCache.
  useEffect(() => {
    for (const { template } of prepCards) {
      if (getTemplateKind(template) === 'order' && !templateItemsCache[template.id]) {
        fetchTemplateItemsForDash(template.id);
      }
    }
  }, [prepCards]); // eslint-disable-line react-hooks/exhaustive-deps

  const containerPadding = isMobile ? '0.75rem' : '1.5rem';
  const isNarrowMobile = isMobile && viewportWidth <= 420;

  // Returns true when zero distinct items have a par_level > 0.
  // Mirrors the same function in CountInterface.jsx so both stay in sync.
  const isParsSetupNeeded = (items) => {
    if (!items || items.length === 0) return false;
    const parByKey = new Map();
    (items || []).forEach(item => {
      const name = (item.item_snapshot?.description || item.item_snapshot?.name || '').trim().toLowerCase();
      const vendor = (item.item_snapshot?.vendor_name || item.item_snapshot?.vendor || item.vendor || '').trim().toLowerCase();
      const key = item.item_id ? `id:${item.item_id}` : `nv:${name}|${vendor}`;
      const hasPar = Number(item.par_level) > 0;
      parByKey.set(key, (parByKey.get(key) || false) || hasPar);
    });
    const withPar = Array.from(parByKey.values()).filter(Boolean).length;
    return withPar === 0;
  };

  // Compute whether a schedule is still missing for a template.
  // Mirrors the same logic in CountInterface.jsx.
  const computeScheduleMissing = (template) => {
    const kind = getTemplateKind(template);
    const isEOP = kind === 'eop';
    const schedCyc = template?.ordering_cycle_config;
    const isSkippedSentinel = !!(schedCyc && schedCyc.preset === 'none' && schedCyc.skipped === true);
    const hasRealCycle = !!(schedCyc && schedCyc.preset && !isSkippedSentinel);
    const eopInheritsStore = isEOP && !!inventoryPeriodConfigToCycle(storePeriodConfig);
    return !hasRealCycle && !isSkippedSentinel && !eopInheritsStore;
  };

  // Fetch and cache count_items for a template. Returns cached result if already loaded.
  const fetchTemplateItemsForDash = async (templateId) => {
    if (!templateId) return [];
    if (templateItemsCache[templateId]) return templateItemsCache[templateId];
    try {
      const { data, error } = await supabase
        .from('count_items')
        .select('*')
        .eq('count_session_id', templateId)
        .order('id');
      if (error) throw error;
      const items = data || [];
      setTemplateItemsCache(prev => ({ ...prev, [templateId]: items }));
      return items;
    } catch (err) {
      console.warn('[PrepCard] failed to fetch template items for pars:', err);
      return [];
    }
  };

  // Opens the pars/MOQ wizard modal for the dashboard and returns a Promise
  // that resolves with 'cancel', { type: 'skip' }, or { type: 'save', rows, allRows }.
  const showDashParsWizard = (template, items, isFirstTimeSetup = false) => {
    return new Promise((resolve) => {
      dashParsResolverRef.current = resolve;
      setDashParsWizardModal({ template, items, isFirstTimeSetup });
    });
  };

  const handlePrepStepOpen = async (template, scheduledDate, stepKey) => {
    const busyKey = `${template.id}:${scheduledDate}:${stepKey}`;
    setPrepCardBusy((prev) => ({ ...prev, [busyKey]: true }));
    const fieldName = `${stepKey}_opened_at`;
    const now = new Date().toISOString();
    const statusKey = `${template.id}:${scheduledDate}`;
    setPrepStatuses((prev) => ({
      ...prev,
      [statusKey]: { ...(prev[statusKey] || {}), [fieldName]: (prev[statusKey]?.[fieldName]) || now }
    }));
    try {
      await supabase.from('count_prep_status').upsert(
        {
          store_id: storeId,
          template_id: template.id,
          scheduled_date: scheduledDate,
          template_kind: getTemplateKind(template),
          [fieldName]: now,
          created_by: currentUser?.id ?? null,
        },
        { onConflict: 'store_id,template_id,scheduled_date', ignoreDuplicates: false }
      );
      // TODO(notifications): fire `prep_step_opened:${stepKey}` event for in-app notification hook
    } catch (err) {
      console.warn('[PrepCard] failed to record step open:', err);
    } finally {
      setPrepCardBusy((prev) => ({ ...prev, [busyKey]: false }));
    }
  };

  const handlePrepStepToggle = async (template, scheduledDate, stepKey, currentlyDone) => {
    const fieldName = `${stepKey}_completed_at`;
    const statusKey = `${template.id}:${scheduledDate}`;
    const now = new Date().toISOString();
    const newValue = currentlyDone ? null : now;
    setPrepStatuses((prev) => ({
      ...prev,
      [statusKey]: { ...(prev[statusKey] || {}), [fieldName]: newValue },
    }));
    try {
      await supabase.from('count_prep_status').upsert(
        {
          store_id: storeId,
          template_id: template.id,
          scheduled_date: scheduledDate,
          template_kind: getTemplateKind(template),
          [fieldName]: newValue,
          created_by: currentUser?.id ?? null,
        },
        { onConflict: 'store_id,template_id,scheduled_date', ignoreDuplicates: false }
      );
      // TODO(notifications): fire `prep_step_toggled:${stepKey}` event for in-app notification hook
    } catch (err) {
      console.warn('[PrepCard] failed to toggle step completion:', err);
      setPrepStatuses((prev) => ({
        ...prev,
        [statusKey]: { ...(prev[statusKey] || {}), [fieldName]: currentlyDone ? now : null },
      }));
    }
  };

  const toggleSectionExpansion = (sectionKey) => {
    setExpandedSections((previous) => ({
      ...previous,
      [sectionKey]: !previous[sectionKey]
    }));
  };

  const toggleCollapsedSection = (sectionKey) => {
    setCollapsedSections((previous) => ({
      ...previous,
      [sectionKey]: !previous[sectionKey]
    }));
  };

  const isSectionCollapsed = (sectionKey) => isMobile && collapsedSections[sectionKey];

  const getVisibleItems = (sectionKey, items) => {
    if (expandedSections[sectionKey]) return items;
    return items.slice(0, PREVIEW_LIMIT);
  };

  const renderExpansionToggle = (sectionKey, totalCount, noun, orderingLabel = 'soonest', tone = 'default') => {
    if (totalCount <= PREVIEW_LIMIT) return null;
    const expanded = expandedSections[sectionKey];
    const showingCount = expanded ? totalCount : PREVIEW_LIMIT;
    const isMutedTone = tone === 'muted';
    return (
      <div style={{
        marginTop: '0.45rem',
        paddingTop: '0.45rem',
        borderTop: `1px solid ${isMutedTone ? '#E7DED5' : COLORS.secondary}`
      }}>
        <div style={{ fontSize: '0.664rem', color: isMutedTone ? COLORS.muted : COLORS.medium, marginBottom: '0.34rem' }}>
          Showing {showingCount} of {totalCount} {orderingLabel}.
        </div>
        <button
          type="button"
          onClick={() => toggleSectionExpansion(sectionKey)}
          style={{
            border: `1px solid ${isMutedTone ? '#D8C9B9' : COLORS.primary}`,
            borderRadius: '8px',
            padding: '0.32rem 0.58rem',
            fontSize: '0.664rem',
            fontWeight: '600',
            color: isMutedTone ? COLORS.medium : COLORS.dark,
            backgroundColor: isMutedTone ? '#F9F5F1' : COLORS.offWhite,
            cursor: 'pointer'
          }}
        >
          {expanded ? `Collapse to ${PREVIEW_LIMIT}` : `Expand all ${totalCount}`} {noun}
        </button>
      </div>
    );
  };

  const renderSectionHeader = ({
    icon,
    title,
    count,
    subtitle,
    tone = 'default',
    kicker,
    sectionKey,
    accent
  }) => {
    const collapsed = sectionKey ? isSectionCollapsed(sectionKey) : false;
    const isCollapsible = isMobile && sectionKey;

    const headingColor = accent?.headingColor
      || (tone === 'muted' ? COLORS.medium : COLORS.dark);
    const kickerColor = accent?.kickerColor
      || (tone === 'muted' ? COLORS.muted : COLORS.medium);
    const badgeBg = accent?.badgeBg
      || (tone === 'primary' ? '#F9EFE2' : tone === 'muted' ? '#F3ECE4' : COLORS.offWhite);
    const badgeColor = accent?.badgeColor
      || (tone === 'muted' ? COLORS.medium : COLORS.dark);
    const badgeBorderColor = accent?.sectionBorder
      || (tone === 'primary' ? '#DECBB7' : COLORS.secondary);

    const headerContent = (
      <div style={{ marginBottom: collapsed ? 0 : '0.75rem' }}>
        {kicker && (
          <div style={{
            marginBottom: '0.25rem',
            fontSize: '0.576rem',
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            fontWeight: '700',
            color: kickerColor
          }}>
            {kicker}
          </div>
        )}
        <h2 style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: isNarrowMobile ? '0.848rem' : isMobile ? '0.936rem' : tone === 'primary' ? '1.47rem' : '1.25rem',
          fontWeight: '700',
          color: headingColor,
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem'
        }}>
          <span style={{
            minWidth: 0,
            ...(isMobile
              ? { overflowWrap: 'anywhere', wordBreak: 'break-word' }
              : { overflowWrap: 'break-word' })
          }}>{icon} {title}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{
              fontSize: '0.624rem',
              borderRadius: '999px',
              padding: '0.18rem 0.58rem',
              backgroundColor: badgeBg,
              color: badgeColor,
              border: `1px solid ${badgeBorderColor}`,
              fontWeight: '700'
            }}>
              {count}
            </span>
            {isCollapsible && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '1.4rem',
                height: '1.4rem',
                fontSize: '0.736rem',
                color: COLORS.medium,
                transition: 'transform 0.3s ease',
                transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)'
              }}>
                ▾
              </span>
            )}
          </span>
        </h2>
        {subtitle && (
          <p style={{
            margin: '0.28rem 0 0',
            color: kickerColor,
            fontSize: isNarrowMobile ? '0.656rem' : isMobile ? '0.704rem' : '0.776rem',
            maxWidth: '60ch',
            lineHeight: 1.45
          }}>
            {subtitle}
          </p>
        )}
      </div>
    );

    if (isCollapsible) {
      return (
        <button
          type="button"
          onClick={() => toggleCollapsedSection(sectionKey)}
          aria-expanded={!collapsed}
          aria-controls={`section-body-${sectionKey}`}
          style={{
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
            background: 'none',
            border: 'none',
            padding: 0,
            margin: 0,
            width: '100%',
            textAlign: 'left',
            font: 'inherit',
            color: 'inherit'
          }}
        >
          {headerContent}
        </button>
      );
    }

    return headerContent;
  };

  const renderCollapsibleBody = (sectionKey, children) => {
    const collapsed = isSectionCollapsed(sectionKey);
    if (!isMobile) return children;
    return (
      <div
        id={`section-body-${sectionKey}`}
        role="region"
        style={{
          overflow: 'hidden',
          maxHeight: collapsed ? '0px' : '5000px',
          opacity: collapsed ? 0 : 1,
          transition: collapsed
            ? 'max-height 0.3s ease, opacity 0.2s ease'
            : 'max-height 0.5s ease, opacity 0.3s ease 0.1s',
        }}
      >
        {children}
      </div>
    );
  };

  const renderSessionMetaGrid = (session, showProgress = false, tone = 'default') => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isNarrowMobile ? '1fr' : isMobile ? '1fr 1fr' : 'repeat(4, minmax(120px, 1fr))',
      gap: '0.45rem',
      marginTop: '0.5rem',
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      <div style={{ backgroundColor: tone === 'muted' ? '#F6F1EC' : '#FDFBF9', border: `1px solid ${tone === 'muted' ? '#E6DBD1' : COLORS.secondary}`, borderRadius: '8px', padding: isNarrowMobile ? '0.35rem 0.45rem' : '0.4rem 0.5rem', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
        <div style={{ fontSize: '0.616rem', color: COLORS.medium }}>Scheduled</div>
        <div style={{ fontSize: isNarrowMobile ? '0.68rem' : '0.72rem', color: COLORS.dark, fontWeight: '600', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{formatDate(session.sessionDate)}</div>
      </div>
      <div style={{ backgroundColor: tone === 'muted' ? '#F6F1EC' : '#FDFBF9', border: `1px solid ${tone === 'muted' ? '#E6DBD1' : COLORS.secondary}`, borderRadius: '8px', padding: isNarrowMobile ? '0.35rem 0.45rem' : '0.4rem 0.5rem', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
        <div style={{ fontSize: '0.616rem', color: COLORS.medium }}>Last Access By</div>
        <div style={{ fontSize: isNarrowMobile ? '0.68rem' : '0.72rem', color: COLORS.dark, fontWeight: '600', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{session.accessedByLabel}</div>
      </div>
      <div style={{ backgroundColor: tone === 'muted' ? '#F6F1EC' : '#FDFBF9', border: `1px solid ${tone === 'muted' ? '#E6DBD1' : COLORS.secondary}`, borderRadius: '8px', padding: isNarrowMobile ? '0.35rem 0.45rem' : '0.4rem 0.5rem', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
        <div style={{ fontSize: '0.616rem', color: COLORS.medium }}>First Access</div>
        <div style={{ fontSize: isNarrowMobile ? '0.68rem' : '0.72rem', color: COLORS.dark, fontWeight: '600', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{formatDateTime(session.firstAccess)}</div>
      </div>
      <div style={{ backgroundColor: tone === 'muted' ? '#F6F1EC' : '#FDFBF9', border: `1px solid ${tone === 'muted' ? '#E6DBD1' : COLORS.secondary}`, borderRadius: '8px', padding: isNarrowMobile ? '0.35rem 0.45rem' : '0.4rem 0.5rem', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
        <div style={{ fontSize: '0.616rem', color: COLORS.medium }}>Last Access</div>
        <div style={{ fontSize: isNarrowMobile ? '0.68rem' : '0.72rem', color: COLORS.dark, fontWeight: '600', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{formatDateTime(session.lastAccess)}</div>
      </div>
      {showProgress && (
        <div style={{ gridColumn: '1 / -1', marginTop: '0.2rem' }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '0.18rem' : '0.35rem',
            marginBottom: '0.25rem',
            fontSize: isMobile ? '0.664rem' : '0.696rem',
            color: COLORS.dark
          }}>
            <span>{session.percent}% complete</span>
            <span style={{
              textAlign: isMobile ? 'left' : 'right',
              overflowWrap: 'anywhere',
              maxWidth: '100%'
            }}>
              {session.uncountedItems} uncounted ({session.countedItems}/{session.totalItems})
            </span>
          </div>
          <div style={{ width: '100%', height: isMobile ? '9px' : '8px', borderRadius: '999px', backgroundColor: '#EFE7DE', overflow: 'hidden' }}>
            <div
              style={{
                width: `${session.percent}%`,
                height: '100%',
                background: session.percent >= 100 ? COLORS.green : COLORS.yellow,
                transition: 'width 160ms ease'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderSinglePrepCard = (template, scheduledDate) => {
    const statusKey = `${template.id}:${scheduledDate}`;
    const status = prepStatuses[statusKey] || {};
    const daysLeft = daysUntilDate(scheduledDate);
    const daysLabel = daysLeft === 0 ? 'TODAY' : daysLeft === 1 ? 'TOMORROW' : `IN ${daysLeft}D`;
    const kind = getTemplateKind(template);
    const kindLabel = kind === 'order' ? '🚚 Order count' : kind === 'eop' ? '📊 EOP count' : '📋 Count';
    const busyPrefix = `${template.id}:${scheduledDate}`;
    const isOrder = kind === 'order';

    // Derive setup flags for this template
    const scheduleMissing = computeScheduleMissing(template);
    const cachedItems = templateItemsCache[template.id] || null;
    const parsMissing = isOrder && cachedItems !== null && cachedItems.length > 0 && isParsSetupNeeded(cachedItems);

    const steps = [];

    // Add PARs — always shown for order templates until manually checked off
    if (isOrder && !status.add_pars_completed_at) {
      steps.push({
        key: 'add_pars',
        label: 'Add PARs',
        openedAt: status.add_pars_opened_at,
        completedAt: status.add_pars_completed_at,
        onClickAction: async () => {
          await handlePrepStepOpen(template, scheduledDate, 'add_pars');
          const templateSnapshot = { ...template };
          try {
            const items = await fetchTemplateItemsForDash(templateSnapshot.id);
            const isFirstTime = isParsSetupNeeded(items);
            const result = await showDashParsWizard(templateSnapshot, items, isFirstTime);
            if (result === 'cancel' || !result || result.type === 'skip') return;
            if (result.type === 'save') {
              const rows = result.rows || [];
              if (rows.length > 0) {
                // Same contract as every PAR writer: the legacy pair moves
                // WITH the normalization quintet (shared builder), degrading
                // to the pair alone on undeployed columns. No minimum rides
                // along — the vendor MOQ lives on purchase_options.
                const writeResults = await Promise.all(rows.map(async (row) => {
                  const legacyPatch = {
                    par_level: row.par_level,
                    par_unit: row.par_unit || 'each',
                  };
                  const full = await supabase.from('count_items')
                    .update({
                      ...legacyPatch,
                      ...buildParNormalizationPatch({
                        parLevel: row.par_level,
                        parUnit: row.par_unit,
                        countUnit: row.item_snapshot?.unit ?? row.count_unit ?? null,
                      }),
                    })
                    .eq('id', row.id);
                  if (full.error && isMissingParNormalizationColumnError(full.error)) {
                    return supabase.from('count_items').update(legacyPatch).eq('id', row.id);
                  }
                  return full;
                }));
                const firstErr = writeResults.find(r => r.error)?.error;
                if (firstErr) throw firstErr;
              }
              // Update the cache with new par values from allRows
              const allRows = result.allRows || rows;
              const parById = {};
              allRows.forEach(r => { parById[r.id] = r; });
              let updatedItems = null;
              setTemplateItemsCache(prev => {
                const existing = prev[templateSnapshot.id];
                if (!existing) return prev;
                const merged = existing.map(item =>
                  parById[item.id]
                    ? { ...item,
                        par_level: parById[item.id].par_level,
                        par_unit: parById[item.id].par_unit,
                      }
                    : item
                );
                updatedItems = merged;
                return { ...prev, [templateSnapshot.id]: merged };
              });
              // Mark complete as soon as at least one item has a PAR
              // (isParsSetupNeeded returns false → done).
              const itemsAfterSave = updatedItems || items;
              if (!isParsSetupNeeded(itemsAfterSave)) {
                await handlePrepStepToggle(template, scheduledDate, 'add_pars', false);
              }
            }
          } catch (e) {
            console.error('[PrepCard] Failed to open/save pars wizard:', e);
          }
        },
      });
    }

    steps.push(
      {
        key: 'review_items',
        label: 'Review items',
        openedAt: status.review_items_opened_at,
        completedAt: status.review_items_completed_at,
        onClickAction: async () => {
          await handlePrepStepOpen(template, scheduledDate, 'review_items');
          window.location.href = `/count-templates.html?edit=${template.id}`;
        },
      },
      {
        key: 'update_prices',
        label: 'Update prices',
        openedAt: status.update_prices_opened_at,
        completedAt: status.update_prices_completed_at,
        onClickAction: async () => {
          await handlePrepStepOpen(template, scheduledDate, 'update_prices');
          window.location.href = `/invoice-inbox.html?template=${template.id}`;
        },
      },
      {
        key: 'create_count',
        label: status.created_session_id ? 'Count created' : 'Create count',
        openedAt: null,
        completedAt: status.create_count_completed_at,
        onClickAction: status.create_count_completed_at
          ? null
          : () => { window.location.href = `/count-templates.html?highlight=${template.id}`; },
      },
    );

    const allDone = steps.every((s) => !!s.completedAt);

    return (
      <div key={statusKey} style={{
        border: allDone ? '1px solid #A8D098' : '1px solid #E8CEAF',
        borderLeft: `3px solid ${allDone ? '#3E7A28' : '#C8880C'}`,
        borderRadius: '10px',
        backgroundColor: allDone ? '#F5FAF2' : '#FFFBF2',
        padding: isMobile ? '0.6rem 0.7rem' : '0.65rem 0.85rem',
        marginBottom: '0.5rem',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.45rem' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontWeight: 700,
              color: allDone ? '#2C601C' : COLORS.dark,
              fontSize: isNarrowMobile ? '0.744rem' : isMobile ? '0.792rem' : '0.84rem',
              lineHeight: 1.2,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              overflowWrap: 'anywhere', wordBreak: 'break-word'
            }}>
              {template.name}
            </div>
            <div style={{ fontSize: '0.6rem', color: COLORS.medium, marginTop: '0.1rem' }}>
              {kindLabel} · {formatDate(scheduledDate + 'T00:00:00')}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.2rem', flexShrink: 0 }}>
            <span style={{
              backgroundColor: daysLeft === 0 ? '#F3DDBB' : daysLeft === 1 ? '#FCE8D0' : '#FEF0C0',
              color: daysLeft === 0 ? '#845D2E' : '#7A5A0A',
              borderRadius: '999px',
              padding: '0.12rem 0.5rem',
              fontSize: '0.56rem',
              fontWeight: 800,
              whiteSpace: 'nowrap',
            }}>
              {daysLabel}
            </span>
            <span style={{
              backgroundColor: allDone ? '#E4EFD9' : '#FEF0C0',
              color: allDone ? '#466935' : '#7A5A0A',
              borderRadius: '999px',
              padding: '0.1rem 0.45rem',
              fontSize: '0.496rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              letterSpacing: '0.02em',
            }}>
              {allDone ? '✓ Ready soon' : 'Prep needed'}
            </span>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isNarrowMobile
            ? 'repeat(2, minmax(0, 1fr))'
            : `repeat(${Math.max(steps.length, 1)}, minmax(0, 1fr))`,
          gap: '0.25rem',
        }}>
          {steps.map((step) => {
            const done = !!step.completedAt;
            const opened = !!step.openedAt && !done;
            const busy = !!prepCardBusy[`${busyPrefix}:${step.key}`];

            return (
              <div key={step.key} style={{
                display: 'flex', alignItems: 'center', gap: '0.2rem',
                background: done ? '#F1F8EE' : opened ? '#FEF0C0' : '#FFFFFF',
                border: done ? '1px solid #CFE3BE' : opened ? '1px solid #E8CEAF' : '1px solid #E0D8CF',
                borderRadius: '6px', padding: '0.22rem 0.35rem',
                minWidth: 0,
              }}>
                <input
                  type="checkbox"
                  checked={done}
                  onChange={() => handlePrepStepToggle(template, scheduledDate, step.key, done)}
                  style={{
                    width: '12px', height: '12px', cursor: 'pointer', flexShrink: 0,
                    accentColor: '#3E7A28', margin: 0,
                  }}
                  title={done ? 'Mark as incomplete' : 'Mark as complete'}
                />
                {done ? (
                  step.key === 'create_count' && status.created_session_id ? (
                    <a href={`/count-session.html?session=${status.created_session_id}`} style={{ fontSize: '0.576rem', color: '#2C601C', textDecoration: 'none', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                      Open →
                    </a>
                  ) : (
                    <span style={{ fontSize: '0.576rem', color: '#2C601C', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{step.label}</span>
                  )
                ) : step.onClickAction ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={step.onClickAction}
                    title={step.label}
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      cursor: busy ? 'wait' : 'pointer',
                      fontFamily: 'inherit',
                      color: opened ? '#7A5A0A' : COLORS.dark,
                      fontSize: '0.576rem', fontWeight: 600,
                      textAlign: 'left', lineHeight: 1.25,
                      opacity: busy ? 0.6 : 1,
                      display: 'flex', alignItems: 'center', gap: '0.15rem',
                      minWidth: 0, flex: 1,
                    }}
                  >
                    {busy ? (
                      <span>…</span>
                    ) : (
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                        {step.label}{opened ? ' •' : ''}
                      </span>
                    )}
                  </button>
                ) : (
                  <span style={{ fontSize: '0.576rem', color: COLORS.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{step.label}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCountCards = (sessions, emptyMessage, showProgress, tone = 'default') => {
    if (sessions.length === 0) {
      return <p style={{ margin: 0, color: tone === 'muted' ? COLORS.muted : COLORS.medium, fontSize: isMobile ? '0.752rem' : '0.824rem' }}>{emptyMessage}</p>;
    }
    return sessions.map((session, index) => {
      const rawStatus = normalizeStatus(session.status || (showProgress ? 'in_progress' : 'scheduled'));
      const statusLabel = rawStatus === 'in_progress'
        ? 'In Progress'
        : rawStatus === 'completed'
          ? 'Completed'
          : rawStatus || 'Scheduled';
      const statusStyles = rawStatus === 'in_progress'
        ? { bg: '#F6E8CE', color: '#7E5930' }
        : rawStatus === 'completed'
          ? { bg: '#E4EFD9', color: '#466935' }
          : { bg: '#E8EEF8', color: '#375F8E' };
      const cardBackground = tone === 'muted'
        ? index % 2 === 0 ? '#FBF7F3' : '#F7F1EC'
        : index % 2 === 0 ? '#FFFFFF' : '#FCFAF8';
      const cardBorder = showProgress ? '#DEC9B5' : tone === 'muted' ? '#E5DBD1' : COLORS.secondary;

      return (
        <div key={session.id} style={{
          border: `1px solid ${cardBorder}`,
          borderRadius: '12px',
          padding: isMobile ? '0.65rem' : '0.85rem',
          backgroundColor: cardBackground,
          marginBottom: '0.62rem',
          boxShadow: showProgress ? '0 6px 16px rgba(90, 74, 66, 0.08)' : '0 1px 2px rgba(0, 0, 0, 0.03)',
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: isMobile ? 'flex-start' : 'center' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontWeight: '700',
                color: COLORS.dark,
                fontSize: isNarrowMobile ? '0.776rem' : isMobile ? '0.824rem' : '0.92rem',
                lineHeight: 1.2,
                whiteSpace: isNarrowMobile ? 'normal' : 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                overflowWrap: 'anywhere'
              }}>
                {session.name || 'Untitled Count'}
              </div>
              <div style={{ marginTop: '0.23rem', fontSize: '0.664rem', color: COLORS.medium, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{
                  borderRadius: '999px',
                  padding: '0.11rem 0.44rem',
                  backgroundColor: statusStyles.bg,
                  color: statusStyles.color,
                  fontWeight: '700',
                  textTransform: 'capitalize'
                }}>
                  {statusLabel}
                </span>
              </div>
            </div>
            <a
              href={`/count-session.html?session=${session.id}`}
              style={{
                fontSize: isNarrowMobile ? '0.608rem' : '0.64rem',
                borderRadius: '8px',
                border: `1px solid ${showProgress ? COLORS.primaryDark : COLORS.primary}`,
                color: COLORS.dark,
                textDecoration: 'none',
                padding: isNarrowMobile ? '0.18rem 0.42rem' : '0.22rem 0.5rem',
                backgroundColor: showProgress ? '#F7EBDD' : COLORS.offWhite,
                whiteSpace: 'nowrap',
                fontWeight: '600'
              }}
            >
              Open
            </a>
          </div>
          {renderSessionMetaGrid(session, showProgress, tone)}
        </div>
      );
    });
  };

  const renderVendorCards = (events, emptyMessage, type) => {
    if (events.length === 0) {
      return <p style={{ margin: 0, color: COLORS.medium, fontSize: isMobile ? '0.752rem' : '0.824rem' }}>{emptyMessage}</p>;
    }
    return events.map((event, index) => (
      <div key={event.key} style={{
        border: `1px solid ${index % 2 === 0 ? '#E1D4C6' : '#E8DED3'}`,
        borderRadius: '12px',
        padding: isMobile ? '0.62rem' : '0.82rem',
        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FCF9F6',
        marginBottom: '0.55rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.03)',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <div style={{ fontWeight: '700', color: COLORS.dark, fontSize: isMobile ? '0.792rem' : '0.864rem', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{event.vendor}</div>
          <span style={{
            backgroundColor: event.daysUntil === 0 ? '#F3DDBB' : '#E3EDD7',
            color: event.daysUntil === 0 ? '#845D2E' : '#486D37',
            borderRadius: '999px',
            padding: '0.13rem 0.5rem',
            fontSize: '0.608rem',
            fontWeight: '700',
            whiteSpace: 'nowrap'
          }}>
            {event.daysUntil === 0 ? 'TODAY' : `IN ${event.daysUntil}D`}
          </span>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isNarrowMobile ? '1fr' : '1fr 1fr',
          gap: '0.4rem',
          marginTop: '0.45rem',
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{ backgroundColor: '#FDFBF9', border: `1px solid ${COLORS.secondary}`, borderRadius: '8px', padding: '0.38rem 0.5rem', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
            <div style={{ fontSize: '0.608rem', color: COLORS.medium }}>{type === 'order' ? 'Order Day' : 'Delivery Day'}</div>
            <div style={{ fontSize: '0.72rem', color: COLORS.dark, fontWeight: '600', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {event.weekdayLabel} • {formatDate(event.date)}
            </div>
          </div>
          <div style={{ backgroundColor: '#FDFBF9', border: `1px solid ${COLORS.secondary}`, borderRadius: '8px', padding: '0.38rem 0.5rem', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
            <div style={{ fontSize: '0.608rem', color: COLORS.medium }}>{type === 'order' ? 'Cutoff/Note' : 'Details'}</div>
            <div style={{ fontSize: '0.72rem', color: COLORS.dark, fontWeight: '600', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {(type === 'order' ? event.cutoffTime : event.notes) || event.notes || (type === 'order' ? 'Not set' : 'Scheduled')}
            </div>
          </div>
        </div>
      </div>
    ));
  };

  const renderRecentPoRows = (pos) => {
    const fmtDelivShort = (d) => {
      try { return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return null; }
    };
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    // Resend retries delayed emails for up to 4 hours before giving up.
    // Only show delivery_delayed in the Failed bucket after that window has passed.
    const DELAY_RECOVERY_WINDOW_MS = 4 * 60 * 60 * 1000;
    const drafts = pos.filter((p) => p.delivery_status === 'draft');
    const blockedNoEmail = pos.filter((p) => p.delivery_status === 'blocked_no_email');
    const failed = pos.filter((p) => {
      if (p.delivery_status === 'delivery_delayed') {
        const delayedAt = p.delayed_at ? new Date(p.delayed_at).getTime() : 0;
        return delayedAt > 0 && (Date.now() - delayedAt) > DELAY_RECOVERY_WINDOW_MS;
      }
      return ['bounced', 'complained', 'error', 'failed', 'blocked'].includes(p.delivery_status);
    });
    const sentToday = pos.filter((p) => p.sent_at && new Date(p.sent_at) >= todayStart && !['draft','blocked_no_email','bounced','complained','error','failed','blocked','delivery_delayed'].includes(p.delivery_status));
    const vendorsMissingContact = setupData?.vendorsMissingEmail || [];
    const renderGroup = (label, color, bg, leftBorder, rows, linkLabel) => {
      if (!rows.length) return null;
      return (
        <>
          <div style={{ fontSize: '0.624rem', fontWeight: 800, color, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 4, marginTop: 6 }}>{label} ({rows.length})</div>
          {rows.map((po, i) => {
            const total = typeof po.total === 'number' ? po.total : parseFloat(po.total) || 0;
            const sentTime = po.sent_at ? new Date(po.sent_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
            const createdTime = po.created_at ? new Date(po.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
            const delivDate = po.requested_delivery_date ? fmtDelivShort(po.requested_delivery_date) : null;
            const items = po.line_count > 0 ? `${po.line_count} item${po.line_count !== 1 ? 's' : ''}` : null;
            const rowPill = getPoStatusPill(po.delivery_status);
            const rowFailure = isFailureStatus(po.delivery_status);
            return (
              <div key={po.id || i} style={{
                border: `1px solid ${bg === '#FFFAFA' ? '#ef9a9a' : '#E1D4C6'}`,
                borderLeft: `4px solid ${leftBorder}`,
                borderRadius: '10px',
                padding: isMobile ? '0.52rem 0.6rem' : '0.6rem 0.75rem',
                backgroundColor: bg,
                marginBottom: '0.4rem',
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                gap: '0.6rem',
                justifyContent: 'space-between',
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, minWidth: 0 }}>
                    {rowFailure && (
                      <span
                        data-po-failure-dot="1"
                        title="Email delivery failed — resend or update the vendor address"
                        aria-label="Email delivery failed"
                        style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '999px', background: '#E64A19', boxShadow: '0 0 0 2px #fff, 0 0 0 3px rgba(230,74,25,0.35)', flexShrink: 0 }}
                      />
                    )}
                    <div style={{ fontWeight: '700', color: COLORS.dark, fontSize: '0.92rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {po.vendor_name_snapshot || 'Unknown Vendor'}
                    </div>
                    <span title={rowPill.helper || undefined} style={{ flexShrink: 0, fontSize: '0.56rem', fontWeight: 700, padding: '2px 7px', borderRadius: '999px', background: rowPill.bg, color: rowPill.fg, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.4 }}>
                      {rowPill.label}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.64rem', color, display: 'flex', gap: '0.46rem', flexWrap: 'wrap', alignItems: 'center', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {po.po_number ? <span style={{ fontWeight: 700 }}>{po.po_number}</span> : null}
                    {sentTime ? <span>· Sent {sentTime}</span> : (createdTime ? <span>· Created {createdTime}</span> : null)}
                    {total > 0 ? <span>· ${total.toFixed(2)}</span> : null}
                    {items ? <span>· {items}</span> : null}
                    {delivDate ? <span>· Deliver {delivDate}</span> : null}
                  </div>
                  {failureReasons[po.id] && (
                    <div style={{
                      fontSize: '0.6rem', color, marginTop: 2, opacity: 0.85, fontStyle: 'italic',
                      minWidth: 0,
                      ...(isMobile
                        ? { overflowWrap: 'anywhere', wordBreak: 'break-word' }
                        : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })
                    }}>
                      {failureReasons[po.id]}
                    </div>
                  )}
                </div>
                <a href={`/ordering${po.po_number ? `?search=${encodeURIComponent(po.po_number)}` : ''}`} style={{
                  fontSize: '0.664rem', fontWeight: 700,
                  color, background: bg === '#FFFAFA' ? '#FDECEA' : (bg === '#fff3e0' ? '#ffe0b2' : COLORS.secondary),
                  borderRadius: 7, padding: '5px 10px', textDecoration: 'none', whiteSpace: 'nowrap', lineHeight: 1.4, flexShrink: 0,
                }}>
                  {linkLabel}
                </a>
              </div>
            );
          })}
        </>
      );
    };
    const hasAny = drafts.length || blockedNoEmail.length || failed.length || sentToday.length || vendorsMissingContact.length || stalledPOs.length;
    if (!hasAny) {
      return (
        <p style={{ margin: 0, color: COLORS.medium, fontSize: isMobile ? '0.752rem' : '0.824rem' }}>
          No vendor order actions needed right now.
        </p>
      );
    }
    return (
      <>
        {renderGroup('Needs Review', '#5d4037', '#FFF8E1', '#e65100', drafts, 'Review Draft ↗')}
        {renderGroup('Failed / Bounced', '#b71c1c', '#FFFAFA', '#b71c1c', failed, 'Fix in Ordering ↗')}
        {stalledPOs.length > 0 && (
          <>
            <div style={{ fontSize: '0.624rem', fontWeight: 800, color: '#6b4c00', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 4, marginTop: 6 }}>Not Opened ({stalledPOs.length})</div>
            {stalledPOs.map((po, i) => {
              const total = typeof po.total === 'number' ? po.total : parseFloat(po.total) || 0;
              const sentTime = po.sent_at ? new Date(po.sent_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
              const bhElapsed = Math.floor(businessHoursElapsed(po.sent_at));
              const stalledLabel = bhElapsed >= 48 ? `${Math.floor(bhElapsed / 8)}+ biz days` : `${bhElapsed}h business hours`;
              return (
                <div key={po.id || i} style={{ border: '1px solid #ffe0a0', borderLeft: '4px solid #f59e0b', borderRadius: '10px', padding: isMobile ? '0.52rem 0.6rem' : '0.6rem 0.75rem', backgroundColor: '#fffbeb', marginBottom: '0.4rem', display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: '0.6rem', justifyContent: 'space-between', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '700', color: COLORS.dark, fontSize: '0.92rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 2, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {po.vendor_name_snapshot || 'Unknown Vendor'}
                    </div>
                    <div style={{ fontSize: '0.64rem', color: '#92400e', display: 'flex', gap: '0.46rem', flexWrap: 'wrap', alignItems: 'center', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {po.po_number ? <span style={{ fontWeight: 700 }}>{po.po_number}</span> : null}
                      {sentTime ? <span>· Sent {sentTime}</span> : null}
                      <span>· Unopened for {stalledLabel}</span>
                      {total > 0 ? <span>· ${total.toFixed(2)}</span> : null}
                    </div>
                  </div>
                  <a href={`/ordering${po.po_number ? `?search=${encodeURIComponent(po.po_number)}` : ''}`} style={{ fontSize: '0.664rem', fontWeight: 700, color: '#92400e', background: '#fde68a', borderRadius: 7, padding: '5px 10px', textDecoration: 'none', whiteSpace: 'nowrap', lineHeight: 1.4, flexShrink: 0 }}>
                    Follow Up ↗
                  </a>
                </div>
              );
            })}
          </>
        )}
        {blockedNoEmail.length > 0 && (
          <>
            <div style={{ fontSize: '0.624rem', fontWeight: 800, color: '#6a1a5e', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 4, marginTop: 6 }}>Missing Vendor Email ({blockedNoEmail.length})</div>
            {blockedNoEmail.map((po, i) => (
              <div key={po.id || i} style={{ border: '1px solid #e1bee7', borderLeft: '4px solid #8e24aa', borderRadius: '10px', padding: isMobile ? '0.45rem 0.5rem' : '0.6rem 0.75rem', backgroundColor: '#f3e5f5', marginBottom: '0.4rem', display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? '0.4rem' : '0.6rem', justifyContent: 'space-between', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '700', color: COLORS.dark, fontSize: isMobile ? '0.76rem' : '0.92rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 2, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{po.vendor_name_snapshot || 'Unknown Vendor'}</div>
                  <div style={{ fontSize: isMobile ? '0.576rem' : '0.64rem', color: '#6a1a5e', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {po.po_number ? <span style={{ fontWeight: 700 }}>{po.po_number}</span> : null}
                    <span>· No order email on file</span>
                    {po.total > 0 ? <span>· ${Number(po.total).toFixed(2)}</span> : null}
                  </div>
                </div>
                <a href="/vendor-editor" style={{ fontSize: isMobile ? '0.576rem' : '0.664rem', fontWeight: 700, color: '#6a1a5e', background: '#e1bee7', borderRadius: 7, padding: isMobile ? '4px 7px' : '5px 10px', textDecoration: 'none', whiteSpace: 'nowrap', lineHeight: 1.4, flexShrink: 0 }}>Add Email ↗</a>
              </div>
            ))}
          </>
        )}
        {vendorsMissingContact.length > 0 && (
          <>
            <div style={{ fontSize: '0.624rem', fontWeight: 800, color: '#6a1a5e', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 4, marginTop: 6 }}>Vendors Missing Contact ({vendorsMissingContact.length})</div>
            {vendorsMissingContact.slice(0, 5).map((v, i) => (
              <div key={v.id || i} style={{ border: '1px solid #e1bee7', borderLeft: '4px solid #ab47bc', borderRadius: '10px', padding: isMobile ? '0.45rem 0.5rem' : '0.6rem 0.75rem', backgroundColor: '#fce4ec', marginBottom: '0.4rem', display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? '0.4rem' : '0.6rem', justifyContent: 'space-between', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '700', color: COLORS.dark, fontSize: isMobile ? '0.76rem' : '0.92rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 2, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{v.name || 'Unknown Vendor'}</div>
                  <div style={{ fontSize: isMobile ? '0.576rem' : '0.64rem', color: '#6a1a5e', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>No name, email, or phone on file — orders cannot be sent.</div>
                </div>
                <button type="button" onClick={() => setVendorContactModal({ id: v.id, name: v.name })} style={{ fontSize: isMobile ? '0.576rem' : '0.664rem', fontWeight: 700, color: '#6a1a5e', background: '#f8bbd0', borderRadius: 7, padding: isMobile ? '4px 7px' : '5px 10px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1.4, flexShrink: 0, fontFamily: 'inherit' }}>Add Contacts</button>
              </div>
            ))}
          </>
        )}
        {renderGroup('Sent Today', '#1b5e20', '#f1f8f1', '#388e3c', sentToday, 'View in Ordering ↗')}
      </>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: containerPadding, fontFamily: "'Open Sans', sans-serif", color: COLORS.dark, width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
        <div style={{ textAlign: 'center', padding: '1.5rem' }}>Loading status dashboard...</div>
      </div>
    );
  }

  return (
    <div style={{
      padding: containerPadding,
      paddingBottom: isMobile
        ? 'calc(110px + env(safe-area-inset-bottom))'
        : containerPadding,
      fontFamily: "'Open Sans', sans-serif",
      color: COLORS.dark,
      backgroundColor: COLORS.page,
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      boxSizing: 'border-box'
    }}>
      <div style={{
        marginBottom: '1.2rem',
        border: `1px solid ${COLORS.secondary}`,
        borderRadius: '14px',
        backgroundColor: '#FFFFFF',
        padding: isMobile ? '0.85rem 0.95rem' : '1.05rem 1.3rem',
        boxShadow: '0 6px 18px rgba(90, 74, 66, 0.06)',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        <h1 style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: isNarrowMobile ? '0.84rem' : isMobile ? '0.92rem' : '1.64rem',
          fontWeight: '700',
          margin: 0,
          color: COLORS.dark,
          lineHeight: isMobile ? 1.2 : 1.15,
          ...(isMobile ? {
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere'
          } : {})
        }}>
          {storeName || 'Your Store'}
        </h1>

        {/* Team row — compact horizontal scroll on mobile, wrapping on desktop */}
        {isMobile ? (
          <div style={{ margin: '0.3rem 0 0' }}>
            <div style={{
              display: 'flex',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              gap: '0.3rem',
              alignItems: 'center',
              WebkitOverflowScrolling: 'touch',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              paddingBottom: '2px'
            }}>
              <span style={{ fontWeight: 600, color: COLORS.dark, fontSize: '0.624rem', flexShrink: 0 }}>Team:</span>
              {teamMembers.map((m, idx) => (
                <span key={`${m.label}-${idx}`} style={{
                  background: m.pending ? 'transparent' : '#FAF3EC',
                  border: `1px ${m.pending ? 'dashed' : 'solid'} ${COLORS.secondary}`,
                  color: m.pending ? COLORS.muted : COLORS.dark,
                  borderRadius: '999px',
                  padding: '0.08rem 0.45rem',
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  opacity: m.pending ? 0.75 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.18rem',
                  flexShrink: 0,
                  whiteSpace: 'nowrap'
                }}>
                  {m.label}
                  {m.pending && (
                    <span style={{ fontWeight: 400, fontSize: '0.576em', opacity: 0.85, fontStyle: 'italic' }}>invited</span>
                  )}
                  {!m.pending && m.welcome && (
                    <span style={{ fontWeight: 600, fontSize: '0.544em', background: '#D1FAE5', color: '#065F46', borderRadius: '999px', padding: '0.05rem 0.4rem', lineHeight: 1.4 }}>New</span>
                  )}
                </span>
              ))}
            </div>
            <a href="/store-settings.html#team" style={{
              display: 'inline-block',
              marginTop: '0.28rem',
              fontSize: '0.592rem',
              fontWeight: 600,
              color: COLORS.medium,
              textDecoration: 'none'
            }}>
              Edit team
            </a>
          </div>
        ) : (
          <div style={{
            margin: '0.3rem 0 0',
            color: COLORS.medium,
            fontSize: '0.808rem',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.35rem',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: 600, color: COLORS.dark }}>Team:</span>
            {teamMembers.map((m, idx) => (
              <span key={`${m.label}-${idx}`} style={{
                background: m.pending ? 'transparent' : '#FAF3EC',
                border: `1px ${m.pending ? 'dashed' : 'solid'} ${COLORS.secondary}`,
                color: m.pending ? COLORS.muted : COLORS.dark,
                borderRadius: '999px',
                padding: '0.1rem 0.55rem',
                fontSize: '0.72rem',
                fontWeight: 600,
                opacity: m.pending ? 0.75 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.2rem'
              }}>
                {m.label}
                {m.pending && (
                  <span style={{ fontWeight: 400, fontSize: '0.576em', opacity: 0.85, fontStyle: 'italic' }}>invited</span>
                )}
                {!m.pending && m.welcome && (
                  <span style={{ fontWeight: 600, fontSize: '0.544em', background: '#D1FAE5', color: '#065F46', borderRadius: '999px', padding: '0.05rem 0.4rem', lineHeight: 1.4 }}>Welcome</span>
                )}
              </span>
            ))}
            <a href="/store-settings.html#team" style={{
              background: 'transparent',
              border: `1px solid ${COLORS.secondary}`,
              color: COLORS.medium,
              borderRadius: '999px',
              padding: '0.1rem 0.55rem',
              fontSize: '0.72rem',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none',
              cursor: 'pointer'
            }}>
              Add / edit team
            </a>
          </div>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
        gap: '1rem',
        alignItems: 'start',
        marginBottom: '1.2rem',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}>
      {/* Setup progress — half-width on desktop, full-width on mobile */}
      <div style={{
        backgroundColor: SECTION_ACCENTS.setup.sectionBg,
        border: `1px solid ${SECTION_ACCENTS.setup.sectionBorder}`,
        borderLeft: `4px solid ${SECTION_ACCENTS.setup.stripe}`,
        borderRadius: '12px',
        padding: isMobile ? '0.7rem 0.8rem' : '0.85rem 1rem',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: isMobile ? 'visible' : 'auto',
        maxHeight: isMobile ? 'none' : '440px'
      }}>
          {/* Header — compact two-row on mobile, row with progress on desktop */}
          {isMobile ? (
            <div style={{ marginBottom: '0.5rem' }}>
              <div style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: isNarrowMobile ? '0.776rem' : '0.856rem',
                color: SECTION_ACCENTS.setup.headingColor,
                marginBottom: '0.18rem'
              }}>
                ⚙ Setup progress
              </div>
              <div style={{ fontSize: '0.624rem', color: SECTION_ACCENTS.setup.kickerColor, lineHeight: 1.4 }}>
                Finish these steps to make ordering and inventory easier.
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.6rem',
              gap: '1rem'
            }}>
              <div style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: '0.944rem',
                color: SECTION_ACCENTS.setup.headingColor
              }}>
                ⚙ Setup progress
              </div>
              <div style={{ fontSize: '0.72rem', color: SECTION_ACCENTS.setup.kickerColor, flexShrink: 0 }}>
                Get the most out of Countertop-App
              </div>
            </div>
          )}

          {(() => {
            const items = [];
            const pluralize = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;
            const catalogReadiness = getSetupCatalogReadiness(setupData.totalItems);

            // Item catalog readiness: very low (<50), mostly done (50-149), or
            // well-populated (≥150). The "mostly done" band still behaves like an
            // action item but shows progress so the user knows how close they are.
            // An empty catalog is also an action item; it must never fall through
            // to the "nothing to do" state.
            if (catalogReadiness.band === 'empty' || catalogReadiness.band === 'started') {
              const isEmptyCatalog = catalogReadiness.band === 'empty';
              items.push({
                key: 'low-item-count',
                tone: 'amber',
                title: 'Add your items',
                mobileTitle: 'Add your items',
                mobileCount: isEmptyCatalog ? 'No items added yet' : null,
                mobileDesc: isEmptyCatalog
                  ? 'Import an invoice, menu, spreadsheet, or add items manually.'
                  : 'Import or add items to build out your catalog.',
                hint: isEmptyCatalog
                  ? 'Start your catalog by importing an invoice, menu, spreadsheet, or adding items manually.'
                  : 'To add more items to your catalog visit Item Catalog.',
                href: '/items.html',
                cta: 'Open Item Catalog →',
                belowNode: (
                  <div style={{ marginTop: '0.5rem' }}>
                    <EmailImportButton
                      storeId={storeId}
                      userEmail={currentUser?.email}
                      tone="amber"
                    />
                  </div>
                )
              });
            } else if (catalogReadiness.band === 'partial') {
              items.push({
                key: 'low-item-count',
                tone: 'amber',
                title: 'Add your items',
                mobileTitle: 'Add your items',
                mobileCount: `${setupData.totalItems} of 150 items added`,
                mobileDesc: 'Keep importing or adding items to reach a fuller catalog.',
                hint: `You have ${setupData.totalItems} of 150 items. Add more to build a complete catalog.`,
                href: '/items.html',
                cta: 'Open Item Catalog →',
                partial: true,
                belowNode: (
                  <div style={{ marginTop: '0.5rem' }}>
                    <EmailImportButton
                      storeId={storeId}
                      userEmail={currentUser?.email}
                      tone="amber"
                    />
                  </div>
                )
              });
            } else if (catalogReadiness.band === 'complete') {
              items.push({
                key: 'low-item-count',
                tone: 'green',
                title: 'Add your items',
                mobileTitle: 'Add your items',
                mobileCount: null,
                mobileDesc: `${setupData.totalItems} items added — your catalog is well populated.`,
                hint: `${setupData.totalItems} items added — your catalog is well populated.`,
                href: '/items.html',
                cta: 'View items'
              });
            }

            // Template setup checks are shown for users who bypassed onboarding/auto-template
            // creation. This is dashboard-only for now; AI setup review will later consume the
            // same readiness data. Shown early because templates are what make ordering and
            // End of Period usable.

            // Missing order templates: store has real items + real vendor names but no order template.
            if (
              setupData.totalItems > 0 &&
              setupData.orderTemplateCount === 0 &&
              setupData.vendorsWithoutOrderTemplates.length > 0
            ) {
              const vw = setupData.vendorsWithoutOrderTemplates;
              const previewVendors = vw.slice(0, 3).map((v) => v.vendorName);
              const moreVendors = vw.length - previewVendors.length;
              const vendorListStr = previewVendors.join(', ') + (moreVendors > 0 ? ` +${moreVendors} more` : '');
              items.push({
                key: 'order-templates',
                tone: 'amber',
                title: 'Create an order template',
                mobileTitle: 'Create an order template',
                mobileCount: `Orders need templates for ${vendorListStr}`,
                mobileDesc: null,
                hint: `It appears you do not have orders set up for these vendors: ${vendorListStr}. Order templates let Countertop turn counts into vendor orders.`,
                href: '/count-templates.html',
                cta: 'Open templates →'
              });
            }

            // Missing End of Period template: store has real items but no EOP template.
            // Not shown for empty stores — the low-item-count warning covers that case.
            if (setupData.totalItems > 0 && setupData.hasEopTemplate === false) {
              items.push({
                key: 'eop-template',
                tone: 'amber',
                title: 'End of Period template',
                mobileTitle: 'End of Period template',
                mobileCount: 'EOP needs a template',
                mobileDesc: null,
                hint: 'It appears you do not have any template set up for End of Period. End of Period counts are used to value ending inventory for reporting.',
                href: '/count-templates.html',
                cta: 'Open templates →'
              });
            }

            // Items missing prices
            if (setupData.itemsMissingPrice > 0) {
              items.push({
                key: 'prices',
                tone: 'amber',
                title: 'Add item prices',
                mobileTitle: 'Add item prices',
                mobileCount: pluralize(setupData.itemsMissingPrice, 'item needs a price', 'items need prices'),
                mobileDesc: 'Prices calculate order totals, inventory value, and price changes.',
                hint: 'Add prices to calculate order totals, value inventory on hand, and track price changes over time.',
                href: '/items.html',
                cta: 'Add prices →'
              });
            } else if (setupData.totalItems > 0) {
              items.push({
                key: 'prices',
                tone: 'green',
                title: 'Add item prices',
                mobileTitle: 'Add item prices',
                mobileCount: null,
                mobileDesc: 'Pricing is complete across your inventory.',
                hint: 'Pricing is complete across your inventory.',
                href: '/items.html',
                cta: 'View items'
              });
            }

            // Vendors missing contact details
            if (setupData.vendorsMissingEmail.length > 0) {
              const vendorCount = setupData.vendorsMissingEmail.length;
              const previewNames = setupData.vendorsMissingEmail.slice(0, 3).map((v) => v.name).join(', ');
              const more = vendorCount > 3 ? ` +${vendorCount - 3} more` : '';
              const examples = previewNames ? ` Examples: ${previewNames}${more}` : '';
              items.push({
                key: 'vendors',
                tone: 'amber',
                title: 'Add vendor contact info',
                mobileTitle: 'Add vendor contact info',
                mobileCount: pluralize(vendorCount, 'vendor needs contact info', 'vendors need contact info'),
                mobileDesc: 'Send purchase orders directly to vendors.',
                hint: `Add vendor contacts so Countertop-App can send purchase orders directly to your vendors.${examples}`,
                href: '/vendor-editor.html',
                cta: 'Add contacts →'
              });
            }

            // Low-PAR ordering sessions
            if (setupData.lowParOrders.length > 0) {
              items.push({
                key: 'par',
                tone: 'amber',
                title: 'Add PAR levels',
                mobileTitle: 'Add PAR levels',
                mobileCount: pluralize(setupData.lowParOrders.length, 'ordering template needs PARs', 'ordering templates need PARs'),
                mobileDesc: 'PARs help suggest reorder quantities.',
                hint: 'Add PARs so suggested reorder quantities can be calculated automatically.',
                list: setupData.lowParOrders.slice(0, 5).map((o) => ({
                  primary: o.name,
                  secondary: `Created ${formatDate(o.created_at)} • ${o.withPar}/${o.total} PARs set`,
                  href: `/count.html?id=${o.id}`
                })),
                href: '/count.html',
                cta: 'Open counts →'
              });
            }

            // Unmapped subcategories for P&L reporting
            if (setupData.unmappedSubcategories > 0) {
              items.push({
                key: 'pnl',
                tone: 'amber',
                title: 'Map P&L categories',
                mobileTitle: 'Map P&L categories',
                mobileCount: pluralize(setupData.unmappedSubcategories, 'subcategory needs mapping', 'subcategories need mapping'),
                mobileDesc: 'Map categories to match your accounting reports.',
                hint: 'Map subcategories to P&L categories so inventory reports align with your accounting.',
                href: '/store-settings.html',
                cta: 'Map categories →'
              });
            }

            if (items.length === 0) {
              return (
                <div style={{
                  padding: '0.55rem 0.75rem',
                  background: SECTION_ACCENTS.completedCounts.sectionBg,
                  border: `1px solid ${SECTION_ACCENTS.completedCounts.sectionBorder}`,
                  borderRadius: '10px',
                  color: SECTION_ACCENTS.completedCounts.headingColor,
                  fontSize: '0.76rem',
                  fontWeight: 600
                }}>
                  ✓ Your setup looks great — nothing to do right now.
                </div>
              );
            }

            const toneStyles = {
              amber: { bg: '#FCF4EA', border: '#E8CEAF', accent: '#8B6135' },
              green: { bg: '#F1F8EE', border: '#CFE3BE', accent: '#456A2F' }
            };

            const setupItemIcons = {
              'low-item-count': { src: '/add-items-icon.svg', alt: 'Add items to catalog icon' },
              par: { src: '/par-icon.png', alt: 'PAR levels icon' },
              prices: { src: '/price-icon.png', alt: 'Price icon' },
              vendors: { src: '/order-send-icon.png', alt: 'Order send icon' }
            };

            // Mobile: priority summary above the cards
            const amberItems = items.filter((it) => it.tone === 'amber');
            const outstandingCount = amberItems.length;
            const stepNameMap = {
              prices: 'Add item prices',
              vendors: 'Add vendor contact info',
              par: 'Add PAR levels',
              pnl: 'Map P&L categories',
              'low-item-count': 'Add your items',
              'order-templates': 'Create an order template',
              'eop-template': 'End of Period template'
            };
            const summaryNames = amberItems.slice(0, 3).map((it) => stepNameMap[it.key] || it.mobileTitle || it.key).join(', ');
            const prioritySummary = outstandingCount > 0
              ? `${outstandingCount} setup step${outstandingCount !== 1 ? 's' : ''} left — ${summaryNames} help${outstandingCount === 1 ? 's' : ''} Countertop-App calculate orders.`
              : null;

            // ─── Mobile card rendering ────────────────────────────────────────
            if (isMobile) {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  {prioritySummary && (
                    <div style={{
                      fontSize: '0.6rem',
                      color: '#7A5C3A',
                      background: '#FDF6EC',
                      border: '1px solid #EDD5AF',
                      borderRadius: '8px',
                      padding: '0.4rem 0.6rem',
                      marginBottom: '0.5rem',
                      lineHeight: 1.45
                    }}>
                      {prioritySummary}
                    </div>
                  )}
                  {items.map((it, cardIdx) => {
                    const t = toneStyles[it.tone] || toneStyles.amber;
                    const firstAmberIdx = items.findIndex((x) => x.tone === 'amber');
                    const isHighPriority = it.tone === 'amber' && cardIdx === firstAmberIdx;
                    // First amber card gets a subtle left accent; rest get plain white
                    const cardBg = '#FFFFFF';
                    const cardBorder = isHighPriority ? '#D9A86C' : '#E8E0D8';
                    const cardLeftBorder = isHighPriority ? `3px solid #C8874A` : `1px solid ${cardBorder}`;
                    const isDividerNeeded = cardIdx > 0;
                    return (
                      <div key={it.key} style={{
                        background: cardBg,
                        borderLeft: cardLeftBorder,
                        borderRight: `1px solid ${cardBorder}`,
                        borderTop: isDividerNeeded ? 'none' : `1px solid ${cardBorder}`,
                        borderBottom: `1px solid ${cardBorder}`,
                        borderRadius: cardIdx === 0
                          ? '10px 10px 0 0'
                          : cardIdx === items.length - 1
                            ? '0 0 10px 10px'
                            : '0',
                        padding: '0.55rem 0.65rem',
                        minHeight: '90px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontWeight: 700,
                              color: isHighPriority ? '#7A4A1E' : COLORS.dark,
                              fontSize: isNarrowMobile ? '0.704rem' : '0.744rem',
                              lineHeight: 1.25
                            }}>
                              {it.mobileTitle || it.title}
                            </div>
                            {it.mobileCount && (
                              <div style={{
                                fontSize: '0.6rem',
                                color: isHighPriority ? '#9A6030' : COLORS.medium,
                                fontWeight: 600,
                                marginTop: '0.08rem'
                              }}>
                                {it.mobileCount}
                              </div>
                            )}
                            {it.mobileDesc && (
                              <div style={{
                                fontSize: '0.584rem',
                                color: COLORS.muted,
                                marginTop: '0.1rem',
                                lineHeight: 1.4
                              }}>
                                {it.mobileDesc}
                              </div>
                            )}
                            {it.cta && (
                              <a href={it.href} style={{
                                display: 'inline-block',
                                marginTop: '0.32rem',
                                fontSize: '0.584rem',
                                fontWeight: 700,
                                color: isHighPriority ? '#7A4A1E' : t.accent,
                                textDecoration: 'none'
                              }}>
                                {it.cta}
                              </a>
                            )}
                          </div>
                          {it.tone === 'green' && (
                            <span style={{ fontSize: '0.8rem', flexShrink: 0, opacity: 0.75 }}>✓</span>
                          )}
                          {it.partial && (
                            <span title="Partially complete" style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '999px',
                              background: 'linear-gradient(180deg, #D49A45 50%, #fff 50%)',
                              border: '1.5px solid #D49A45',
                              flexShrink: 0,
                              display: 'inline-block'
                            }} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }

            // ─── Desktop card rendering ────────────────────────────────────────
            const amberItemsDesktop = items.filter((it) => it.tone === 'amber');
            const greenItemsDesktop = items.filter((it) => it.tone === 'green');
            const partialItemsDesktop = items.filter((it) => it.partial && it.tone !== 'green');
            const totalSteps = items.length;
            const doneSteps = greenItemsDesktop.length;
            const progressCount = doneSteps + (partialItemsDesktop.length * 0.5);
            const progressPct = totalSteps > 0 ? Math.round((progressCount / totalSteps) * 100) : 0;

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                {/* Progress summary bar */}
                {totalSteps > 0 && (
                  <div style={{ marginBottom: '0.25rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.28rem' }}>
                      <span style={{ fontSize: '0.656rem', color: SECTION_ACCENTS.setup.kickerColor, fontWeight: 600 }}>
                        {doneSteps} of {totalSteps}
                      </span>
                      <span style={{ fontSize: '0.624rem', color: SECTION_ACCENTS.setup.kickerColor }}>
                        {amberItemsDesktop.length > 0
                          ? `${amberItemsDesktop.length} remaining`
                          : 'All complete'}
                      </span>
                    </div>
                    <div style={{ width: '100%', height: '6px', borderRadius: '999px', backgroundColor: '#EDD596', overflow: 'hidden' }}>
                      <div style={{
                        width: `${progressPct}%`,
                        height: '100%',
                        background: progressPct >= 100 ? SECTION_ACCENTS.completedCounts.stripe : SECTION_ACCENTS.setup.progressBar,
                        transition: 'width 300ms ease',
                        borderRadius: '999px'
                      }} />
                    </div>
                  </div>
                )}
                {/* Amber (action-needed) items — full weight */}
                {amberItemsDesktop.map((it) => {
                  const icon = setupItemIcons[it.key];
                  return (
                    <div key={it.key} style={{
                      background: '#FFFFFF',
                      border: `1px solid ${SECTION_ACCENTS.setup.sectionBorder}`,
                      borderLeft: `3px solid ${SECTION_ACCENTS.setup.stripe}`,
                      borderRadius: '9px',
                      padding: '0.55rem 0.7rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.45rem', flex: 1, minWidth: 0 }}>
                          {icon && (
                            <img
                              src={icon.src}
                              alt={icon.alt}
                              style={{ width: 28, height: 28, flexShrink: 0, marginTop: '0.05rem', opacity: 0.85 }}
                            />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div title={it.title} style={{ fontWeight: 700, color: SECTION_ACCENTS.setup.headingColor, fontSize: '0.776rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                              {it.title}
                              {it.partial && (
                                <span title="Partially complete" style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '999px',
                                  background: 'linear-gradient(180deg, #D49A45 50%, #fff 50%)',
                                  border: '1.5px solid #D49A45',
                                  flexShrink: 0,
                                  display: 'inline-block'
                                }} />
                              )}
                            </div>
                            {it.hint && (
                              <div title={it.hint} style={{ color: COLORS.medium, fontSize: '0.72rem', marginTop: '0.14rem', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {it.hint}
                              </div>
                            )}
                          </div>
                        </div>
                        {it.actionsNode ? it.actionsNode : (it.cta && (
                          <a href={it.href} style={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: SECTION_ACCENTS.setup.headingColor,
                            background: SECTION_ACCENTS.setup.badgeBg,
                            border: `1px solid ${SECTION_ACCENTS.setup.sectionBorder}`,
                            borderRadius: '999px',
                            padding: '0.22rem 0.65rem',
                            textDecoration: 'none',
                            whiteSpace: 'nowrap'
                          }}>{it.cta}</a>
                        ))}
                      </div>
                      {it.belowNode || null}
                      {it.list && it.list.length > 0 && (
                        <ul style={{
                          margin: '0.45rem 0 0',
                          padding: 0,
                          listStyle: 'none',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.22rem'
                        }}>
                          {it.list.map((row, idx) => (
                            <li key={idx} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '0.5rem',
                              background: SECTION_ACCENTS.setup.sectionBg,
                              border: `1px solid ${SECTION_ACCENTS.setup.sectionBorder}`,
                              borderRadius: '7px',
                              padding: '0.3rem 0.5rem'
                            }}>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontWeight: 600, color: COLORS.dark, fontSize: '0.744rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {row.primary}
                                </div>
                                <div title={row.secondary} style={{ fontSize: '0.664rem', color: COLORS.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {row.secondary}
                                </div>
                              </div>
                              {row.href && (
                                <a href={row.href} style={{
                                  fontSize: '0.656rem',
                                  fontWeight: 700,
                                  color: SECTION_ACCENTS.setup.headingColor,
                                  textDecoration: 'none',
                                  whiteSpace: 'nowrap'
                                }}>Open →</a>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
                {/* Green (done) items — de-emphasized, compact */}
                {greenItemsDesktop.length > 0 && (
                  <div style={{
                    marginTop: amberItemsDesktop.length > 0 ? '0.1rem' : 0,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.3rem'
                  }}>
                    {greenItemsDesktop.map((it) => (
                      <span key={it.key} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        background: SECTION_ACCENTS.completedCounts.sectionBg,
                        border: `1px solid ${SECTION_ACCENTS.completedCounts.sectionBorder}`,
                        borderRadius: '999px',
                        padding: '0.18rem 0.6rem',
                        fontSize: '0.64rem',
                        color: SECTION_ACCENTS.completedCounts.headingColor,
                        fontWeight: 600
                      }}>
                        <span style={{ opacity: 0.8 }}>✓</span>
                        {it.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
      </div>

      <DashboardActivityPanel invoices={invoiceActivityRows} priceChanges={priceChangeRows} />
      </div>

      <DashboardMessages
        isMobile={isMobile}
        sectionStyle={{
          backgroundColor: SECTION_ACCENTS.messages.sectionBg,
          border: `1px solid ${SECTION_ACCENTS.messages.sectionBorder}`,
          borderLeft: `4px solid ${SECTION_ACCENTS.messages.stripe}`,
          marginBottom: '1.5rem',
        }}
      />

      <PriceChangeTicker entries={tickerEntries} />

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.35fr 1fr',
        gap: '1.25rem',
        marginTop: '0.5rem',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box'
      }}>
        <div style={{ display: 'grid', gap: '1.25rem', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>

          <section style={{
            backgroundColor: SECTION_ACCENTS.upcomingCounts.sectionBg,
            border: `1px solid ${SECTION_ACCENTS.upcomingCounts.sectionBorder}`,
            borderLeft: `4px solid ${SECTION_ACCENTS.upcomingCounts.stripe}`,
            borderRadius: '14px',
            padding: isMobile ? '0.8rem' : '1rem',
            boxShadow: '0 2px 8px rgba(30, 136, 136, 0.06)',
            minWidth: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden'
          }}>
            {renderSectionHeader({
              icon: '📅',
              title: 'Upcoming Counts',
              count: sessionBuckets.upcoming.length,
              subtitle: 'Next 14 days that have not started yet.',
              kicker: 'Coming Up Soon',
              sectionKey: 'upcoming',
              accent: SECTION_ACCENTS.upcomingCounts
            })}
            {renderCollapsibleBody('upcoming', (() => {
              const visible = getVisibleItems('upcoming', sessionBuckets.upcoming);
              const orderCounts = visible.filter(s => s.order_calculations_enabled === true);
              const nonOrderCounts = visible.filter(s => s.order_calculations_enabled !== true);
              const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              const DOW_S = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
              const todayD = new Date().getDay();
              const ordersByDow = {};
              orderCounts.forEach(s => {
                const d = s.session_date ? new Date(s.session_date + 'T00:00:00') : new Date(s.created_at);
                const dow = d.getDay();
                if (!ordersByDow[dow]) ordersByDow[dow] = [];
                ordersByDow[dow].push(s);
              });
              const ordDays = [];
              for (let i = 0; i < 7; i++) {
                const di = (todayD + i) % 7;
                if (ordersByDow[di]?.length) ordDays.push({ di, label: di === todayD ? `Today (${DOW_S[di]})` : DOW[di], sessions: ordersByDow[di] });
              }

              const hasSessionCards = nonOrderCounts.length > 0 || orderCounts.length > 0;
              return (<>
                {prepCards.length > 0 && (
                  <div style={{ marginBottom: hasSessionCards ? '0.75rem' : 0 }}>
                    <div style={{
                      fontSize: '0.6rem', fontWeight: 800, color: '#7A5A0A',
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      marginBottom: '0.42rem',
                      display: 'flex', alignItems: 'center', gap: '0.4rem'
                    }}>
                      📋 Pre-count prep
                      <span style={{
                        fontSize: '0.504rem', background: '#FEF0C0', color: '#7A5A0A',
                        borderRadius: '999px', padding: '1px 7px', fontWeight: 700
                      }}>
                        next 3 days
                      </span>
                    </div>
                    {prepCards.map(({ template, scheduledDate }) =>
                      renderSinglePrepCard(template, scheduledDate)
                    )}
                    {hasSessionCards && (
                      <div style={{ borderTop: '1px solid #D5C9BF', margin: '0.25rem 0 0.6rem' }} />
                    )}
                  </div>
                )}
                {nonOrderCounts.length > 0 && renderCountCards(nonOrderCounts, '', false, 'secondary')}
                {orderCounts.length > 0 && (
                  <div style={{ marginTop: nonOrderCounts.length > 0 ? '0.5rem' : 0 }}>
                    <div style={{ fontSize: '0.64rem', fontWeight: 700, color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.34rem' }}>🚚 Order Counts by Day</div>
                    {ordDays.map(({ di, label, sessions: ds }) => (
                      <div key={di}>
                        <div style={{ fontSize: '0.608rem', fontWeight: 700, color: '#A89890', padding: '4px 0 2px', borderBottom: '1px solid #E8DDD0', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {label}
                          <span style={{ fontSize: '0.512rem', padding: '1px 5px', borderRadius: '6px', backgroundColor: '#F5F0EB', color: '#8B7B73', fontWeight: 600 }}>{ds.length}</span>
                        </div>
                        {renderCountCards(ds, '', false, 'secondary')}
                      </div>
                    ))}
                  </div>
                )}
                {visible.length === 0 && prepCards.length === 0 && (
                  <p style={{ margin: 0, color: COLORS.medium, fontSize: isMobile ? '0.752rem' : '0.824rem' }}>No upcoming counts in the next 14 days.</p>
                )}
                {renderExpansionToggle('upcoming', sessionBuckets.upcoming.length, 'counts')}
              </>);
            })())}
          </section>

          <section style={{
            backgroundColor: SECTION_ACCENTS.completedCounts.sectionBg,
            border: `1px solid ${SECTION_ACCENTS.completedCounts.sectionBorder}`,
            borderLeft: `4px solid ${SECTION_ACCENTS.completedCounts.stripe}`,
            borderRadius: '14px',
            padding: isMobile ? '0.76rem' : '0.92rem',
            boxShadow: '0 2px 8px rgba(62, 122, 40, 0.05)',
            minWidth: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden'
          }}>
            {renderSectionHeader({
              icon: '✅',
              title: 'Recently Completed Counts',
              count: sessionBuckets.completed.length,
              subtitle: 'Most recent completed sessions for confirmation and follow-up.',
              kicker: 'Logbook',
              sectionKey: 'completed',
              accent: SECTION_ACCENTS.completedCounts
            })}
            {renderCollapsibleBody('completed', <>
              {renderCountCards(
                getVisibleItems('completed', sessionBuckets.completed),
                'No recently completed counts found.',
                false,
                'muted'
              )}
              {renderExpansionToggle('completed', sessionBuckets.completed.length, 'counts', 'most recent', 'muted')}
            </>)}
          </section>
        </div>

        <aside style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>

          <section style={{
            backgroundColor: SECTION_ACCENTS.vendorActions.sectionBg,
            border: `1px solid ${SECTION_ACCENTS.vendorActions.sectionBorder}`,
            borderLeft: `4px solid ${SECTION_ACCENTS.vendorActions.stripe}`,
            borderRadius: '14px',
            padding: isMobile ? '0.6rem' : '0.9rem',
            minWidth: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(184, 72, 40, 0.06)'
          }}>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: isMobile ? 6 : 8, marginBottom: 4 }}>
                <div style={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
                  {renderSectionHeader({
                    icon: '📨',
                    title: 'Vendor Order Actions',
                    count: recentPOs.length,
                    subtitle: 'Drafts to review, failed or bounced POs to fix, and orders sent today.',
                    kicker: 'Action Needed',
                    sectionKey: 'recentOrders',
                    accent: SECTION_ACCENTS.vendorActions
                  })}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', width: isMobile ? '100%' : 'auto', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
                  <a href="/vendor-editor" style={{ fontSize: isMobile ? '0.592rem' : '0.624rem', fontWeight: 700, color: SECTION_ACCENTS.vendorActions.headingColor, background: SECTION_ACCENTS.vendorActions.badgeBg, border: `1px solid ${SECTION_ACCENTS.vendorActions.sectionBorder}`, borderRadius: 7, padding: isMobile ? '4px 7px' : '4px 9px', textDecoration: 'none', whiteSpace: isMobile ? 'normal' : 'nowrap', lineHeight: 1.4, flex: isMobile ? '1 1 auto' : '0 0 auto', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', textAlign: 'center', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    Fix Vendor Contacts ↗
                  </a>
                  <a href="/ordering" style={{ fontSize: isMobile ? '0.592rem' : '0.624rem', fontWeight: 700, color: SECTION_ACCENTS.vendorActions.headingColor, background: SECTION_ACCENTS.vendorActions.badgeBg, border: `1px solid ${SECTION_ACCENTS.vendorActions.sectionBorder}`, borderRadius: 7, padding: isMobile ? '4px 7px' : '4px 9px', textDecoration: 'none', whiteSpace: isMobile ? 'normal' : 'nowrap', lineHeight: 1.4, flex: isMobile ? '1 1 auto' : '0 0 auto', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', textAlign: 'center', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    Open Ordering ↗
                  </a>
                </div>
              </div>
              {renderCollapsibleBody('recentOrders', <>
                {renderRecentPoRows(
                  expandedSections.recentOrders ? recentPOs : recentPOs.slice(0, PREVIEW_LIMIT)
                )}
                {renderExpansionToggle('recentOrders', recentPOs.length, 'orders', 'most recent')}
              </>)}
            </section>

          <section style={{
            backgroundColor: SECTION_ACCENTS.vendorUpcoming.sectionBg,
            border: `1px solid ${SECTION_ACCENTS.vendorUpcoming.sectionBorder}`,
            borderLeft: `4px solid ${SECTION_ACCENTS.vendorUpcoming.stripe}`,
            borderRadius: '14px',
            padding: isMobile ? '0.72rem' : '0.9rem',
            boxShadow: '0 2px 8px rgba(156, 128, 8, 0.06)',
            minWidth: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden'
          }}>
              {renderSectionHeader({
                icon: '🛒',
                title: 'Upcoming Vendor Orders',
                count: upcomingOrders.length,
                subtitle: 'Week ahead from ordering days and custom schedules.',
                kicker: 'Planning',
                sectionKey: 'orders',
                accent: SECTION_ACCENTS.vendorUpcoming
              })}
              {renderCollapsibleBody('orders', <>
                {renderVendorCards(
                  getVisibleItems('orders', upcomingOrders),
                  'No vendor orders scheduled in the next 7 days.',
                  'order'
                )}
                {renderExpansionToggle('orders', upcomingOrders.length, 'vendor orders')}
              </>)}
          </section>

        </aside>
      </div>
      {vendorContactModal && (
        <VendorContactModal
          vendorId={vendorContactModal.id || null}
          vendorName={vendorContactModal.name || null}
          storeId={storeId}
          onClose={() => setVendorContactModal(null)}
          onSaved={() => {
            setVendorContactModal(null);
            if (storeId) loadData(storeId);
          }}
        />
      )}

      {dashParsWizardModal && (
        <ParsWizardModal
          template={dashParsWizardModal.template}
          items={dashParsWizardModal.items}
          isFirstTimeSetup={dashParsWizardModal.isFirstTimeSetup}
          parsOnly={true}
          onSave={(changedRows, allRows) => {
            setDashParsWizardModal(null);
            if (dashParsResolverRef.current) {
              dashParsResolverRef.current({ type: 'save', rows: changedRows, allRows });
              dashParsResolverRef.current = null;
            }
          }}
          onSkip={() => {
            setDashParsWizardModal(null);
            if (dashParsResolverRef.current) {
              dashParsResolverRef.current({ type: 'skip' });
              dashParsResolverRef.current = null;
            }
          }}
          onCancel={() => {
            setDashParsWizardModal(null);
            if (dashParsResolverRef.current) {
              dashParsResolverRef.current('cancel');
              dashParsResolverRef.current = null;
            }
          }}
        />
      )}

    </div>
  );
};

export default TaskAlertDashboard;
